/* ============================================================
   WORKSPACE  (/workspace-v3.9)  — SESAAT Studio v3.9
   ============================================================
   Struktur dan komponen SAMA dengan FormulaShelfView (/formula):
   PageHeader di atas, Segmented untuk tab, token --s-* untuk semua
   warna. Jangan tulis kod warna di sini.

   Rupa senarai mengikut keputusan reka bentuk 2026-08-07:
     · produk = BARIS rata, dipisah garis nipis — bukan kad bertindan
     · nama produk satu-satunya teks terang; ID, link dan tag
       menyepi jadi satu baris bertitik
     · tiada kotak pada tag — kotak berwarna itulah punca kad lama
       nampak sibuk
     · bar tindakan senyap, Cancel jadi tanda silang di hujung
       (dulu ia terpelanting ke baris kedua)

   ⚠️ PROTOTAIP — data di bawah adalah CONTOH TETAP. Halaman ini
   TIDAK menyentuh Firestore, TIDAK menjana apa-apa, dan TIDAK
   berkongsi state dengan /workspace sebenar. Ia untuk menilai
   reka bentuk sahaja. Bila reka bentuk diluluskan, barulah
   WorkspaceView sebenar dipindahkan ke sini.
   ============================================================ */

import { useEffect, useMemo, useState } from 'react';
import {
    ChevronRight, X, GitMerge, Folder,
    Smile, Copy, CloudUpload, VideoOff, Trash2, Play, Check, UploadCloud, Lock, StickyNote,
    CalendarClock, Plus, Send, Link2, RotateCcw,
    ChevronsUpDown, ChevronsDownUp,
    PanelRightClose, PanelRightOpen,
} from 'lucide-react';

import '../design/tokens.css';
import { Badge, Btn, PageHeader, Segmented, EmptyState } from '../design/ui';
import { useT } from '../i18n';
import PromptComposerV39 from './PromptComposerV39';
import ScheduleTemplateModalV39 from './ScheduleTemplateModalV39';
import JadualTiktokV39 from './JadualTiktokV39';
import GroupSwitcherV39, { GROUP_UTAMA, PadamGroupModalV39 } from './GroupSwitcherV39';
import SusunMenuV39 from './SusunMenuV39';
import PilihV39 from './PilihV39';
import WorkflowPilihV39 from './WorkflowPilihV39';
import {
    JADUAL_CONTOH, HAD_HARI_TIKTOK,
    ringkasJadual, susunVideo, letakMasa, tarikhLabel, minitKeJam,
    akaunBaharu, setDipegangAkaunLain,
} from './scheduleV39';

const cx = (...parts) => parts.filter(Boolean).join(' ');

/* ---------- Data contoh -------------------------------------------- */

/* Group = rak produk. Senarai ini sengaja mengandungi rak kosong: pada
   akaun sebenar memang begitu rupanya (rak ujian dan rak yang produknya
   sudah dipindahkan), dan menu ini mesti kekal boleh dibaca dalam
   keadaan itu.

   ⚠️ `id` di sini rekaan prototaip. Pada data sebenar tiada id — group
      ialah NAMA yang tersimpan pada setiap produk (`workspaceGroup`),
      dan group utama bernama `Workspace`, dipapar sebagai "Main Group". */
/* `namaKunci` menyimpan KUNCI kamus, bukan ayat: pemalar aras-modul
   tidak boleh memanggil useT(), jadi nama group utama diterjemah pada
   masa render dalam `groupsUntukMenu`. Group lain memakai `nama` —
   ia nama yang pelanggan sendiri taip, jadi ia tidak diterjemah. */
const GROUP_CONTOH = [
    { id: GROUP_UTAMA, namaKunci: 'workspace.page.groupUtama' },
    { id: 'raya', nama: 'Raya Campaign' },
    { id: 'skin', nama: 'Skincare Q3' },
    { id: 'perabot', nama: 'Perabot Rumah' },
    { id: 'tai8', nama: 'Test AI8SAAT' },
    { id: 'tgoy', nama: 'Test GOYANG' },
];

const CONTOH = [
    {
        id: 'p1', tab: 'flow', group: GROUP_UTAMA, sku: '1729535475451004628',
        name: 'PERFUME PARADISE PEN FOR WOMEN — Packaging Baru (10 ml)',
        host: 'shop.tiktok.com', formula: 'AI8SAAT', avatar: true, gender: 'Women',
        thumb: 'linear-gradient(135deg,#f9a8d4,#be185d)', videos: [1, 0, 0],
    },
    {
        id: 'p2', tab: 'flow', group: GROUP_UTAMA, sku: '1729480091223841902',
        name: 'Set Tudung Bawal Premium Cotton Voal',
        host: 'shop.tiktok.com', formula: 'GOYANG', avatar: false, gender: null,
        thumb: 'linear-gradient(135deg,#fcd34d,#b45309)', videos: [1, 1, 0],
    },
    {
        id: 'p3', tab: 'tiktok', group: GROUP_UTAMA, sku: '1729479113004472281',
        name: 'Minyak Wangi Attar Musk Al-Haramain 12ml',
        host: 'shop.tiktok.com', formula: 'AI8SAAT', avatar: true, gender: 'Men',
        thumb: 'linear-gradient(135deg,#a5b4fc,#4338ca)', videos: [1, 1, 1],
    },
    {
        id: 'p4', tab: 'flow', group: GROUP_UTAMA, sku: '1729478550118822943',
        name: 'Umpan Semut Ant Bait Roach Killer Gel 10g',
        host: 'shop.tiktok.com', formula: 'HOME NICHE', avatar: false, gender: null,
        thumb: 'linear-gradient(135deg,#86efac,#15803d)', videos: [0, 0, 0],
    },
    {
        id: 'p5', tab: 'tiktok', group: 'raya', sku: '1729477221950113374',
        name: 'DRY Pro Baju Hujan Motorcycle Rainsuit Coat',
        host: 'shop.tiktok.com', formula: 'AI8SAAT', avatar: true, gender: 'Men',
        thumb: 'linear-gradient(135deg,#fca5a5,#b91c1c)', videos: [1, 1, 0],
    },
    {
        id: 'p6', tab: 'chatgpt', group: GROUP_UTAMA, sku: '1729476003881224165',
        name: 'Official Glow Skin Toner Hydrating Brightening 100ml',
        host: 'shop.tiktok.com', formula: 'FLOWNITY', avatar: true, gender: 'Women',
        thumb: 'linear-gradient(135deg,#67e8f9,#0e7490)', videos: [1, 0, 0],
    },

    /* Rak utama, tab Flow — senarai sengaja PANJANG (arahan user
       2026-08-12). Tiga baris tidak cukup untuk menilai reka bentuk:
       senarai pendek tidak pernah menatal, jadi kepala halaman yang
       melekat, bar tindakan pukal semasa menatal, dan rupa baris ke-15
       ke bawah semuanya tidak dapat dilihat. Nama produk pula sengaja
       berbeza-beza panjang — nama panjang itulah yang memecahkan susun
       atur baris kalau lebarnya tersilap.

       Keadaan video juga dipelbagaikan (0/3 · 1/3 · 2/3 · 3/3) supaya
       setiap rupa petak video muncul sekurang-kurangnya sekali dalam
       satu skrin. */
    {
        id: 'p13', tab: 'flow', group: GROUP_UTAMA, sku: '1729467941120993284',
        name: 'Botol Air Sukan 1.5L Tritan BPA Free',
        host: 'shop.tiktok.com', formula: 'HOME NICHE', avatar: false, gender: null,
        thumb: 'linear-gradient(135deg,#7dd3fc,#0369a1)', videos: [0, 0, 0],
    },
    {
        id: 'p14', tab: 'flow', group: GROUP_UTAMA, sku: '1729466815773440152',
        name: 'Blouse Muslimah Lengan Puff Crepe Premium',
        host: 'shop.tiktok.com', formula: 'GOYANG', avatar: false, gender: null,
        thumb: 'linear-gradient(135deg,#d8b4fe,#6b21a8)', videos: [1, 1, 1],
    },
    {
        id: 'p15', tab: 'flow', group: GROUP_UTAMA, sku: '1729465702664218937',
        name: 'Serbuk Kopi Tanpa Gula Slim Coffee 15 Sachet',
        host: 'shop.tiktok.com', formula: 'AI8SAAT', avatar: true, gender: 'Women',
        thumb: 'linear-gradient(135deg,#fed7aa,#9a3412)', videos: [1, 0, 0],
    },
    {
        id: 'p16', tab: 'flow', group: GROUP_UTAMA, sku: '1729464590215773408',
        name: 'Vacuum Cleaner Kereta Wireless 12000Pa Portable',
        host: 'shop.tiktok.com', formula: 'HOME NICHE', avatar: false, gender: null,
        thumb: 'linear-gradient(135deg,#cbd5e1,#334155)', videos: [1, 1, 0],
    },
    {
        id: 'p17', tab: 'flow', group: GROUP_UTAMA, sku: '1729463477806112659',
        name: 'Jam Tangan Lelaki Chronograph Stainless Steel Original',
        host: 'shop.tiktok.com', formula: 'AI8SAAT', avatar: true, gender: 'Men',
        thumb: 'linear-gradient(135deg,#bfdbfe,#1e40af)', videos: [1, 1, 1],
    },
    {
        id: 'p18', tab: 'flow', group: GROUP_UTAMA, sku: '1729462364991037215',
        name: 'Set Pinggan Mangkuk Melamine 12 pcs Corak Bunga',
        host: 'shop.tiktok.com', formula: 'HOME NICHE', avatar: false, gender: null,
        thumb: 'linear-gradient(135deg,#99f6e4,#115e59)', videos: [0, 0, 0],
    },
    {
        id: 'p19', tab: 'flow', group: GROUP_UTAMA, sku: '1729461250773668140',
        name: 'Facial Wash Charcoal Deep Clean Anti Jerawat 100ml',
        host: 'shop.tiktok.com', formula: 'FLOWNITY', avatar: true, gender: 'Women',
        thumb: 'linear-gradient(135deg,#f5d0fe,#a21caf)', videos: [1, 0, 0],
    },
    {
        id: 'p20', tab: 'flow', group: GROUP_UTAMA, sku: '1729460138224509371',
        name: 'Kasut Sekolah Putih Anti-Slip Tapak Getah',
        host: 'shop.tiktok.com', formula: 'GOYANG', avatar: false, gender: null,
        thumb: 'linear-gradient(135deg,#e2e8f0,#475569)', videos: [1, 1, 0],
    },
    {
        id: 'p21', tab: 'flow', group: GROUP_UTAMA, sku: '1729459025116883024',
        name: 'Powerbank 20000mAh Fast Charge PD 22.5W',
        host: 'shop.tiktok.com', formula: 'AI8SAAT', avatar: true, gender: 'Men',
        thumb: 'linear-gradient(135deg,#a5f3fc,#155e75)', videos: [1, 1, 1],
    },
    {
        id: 'p22', tab: 'flow', group: GROUP_UTAMA, sku: '1729457913450227896',
        name: 'Tudung Sarung Instant Ironless Premium Cotton',
        host: 'shop.tiktok.com', formula: 'GOYANG', avatar: false, gender: null,
        thumb: 'linear-gradient(135deg,#fbcfe8,#9d174d)', videos: [0, 0, 0],
    },
    {
        id: 'p23', tab: 'flow', group: GROUP_UTAMA, sku: '1729456800337614502',
        name: 'Botol Spray Minyak Masak Air Fryer 500ml',
        host: 'shop.tiktok.com', formula: 'HOME NICHE', avatar: false, gender: null,
        thumb: 'linear-gradient(135deg,#bef264,#4d7c0f)', videos: [1, 0, 0],
    },
    {
        id: 'p24', tab: 'flow', group: GROUP_UTAMA, sku: '1729455688129046773',
        name: 'Serum Rambut Gugur Hair Growth Ginger Extract 60ml',
        host: 'shop.tiktok.com', formula: 'FLOWNITY', avatar: true, gender: 'Women',
        thumb: 'linear-gradient(135deg,#e9d5ff,#7e22ce)', videos: [1, 1, 0],
    },
    {
        id: 'p25', tab: 'flow', group: GROUP_UTAMA, sku: '1729454575012338619',
        name: 'Beg Galas Lelaki Waterproof USB Charging Port',
        host: 'shop.tiktok.com', formula: 'AI8SAAT', avatar: true, gender: 'Men',
        thumb: 'linear-gradient(135deg,#94a3b8,#1e293b)', videos: [1, 0, 0],
    },
    {
        id: 'p26', tab: 'flow', group: GROUP_UTAMA, sku: '1729453462905771248',
        name: 'Lampu Meja LED Rechargeable Eye Protection Dimmable',
        host: 'shop.tiktok.com', formula: 'HOME NICHE', avatar: false, gender: null,
        thumb: 'linear-gradient(135deg,#fde047,#a16207)', videos: [1, 1, 1],
    },
    {
        id: 'p27', tab: 'flow', group: GROUP_UTAMA, sku: '1729452349887214063',
        name: 'Sarung Peluk Bantal Kartun Bulu Lembut 45cm',
        host: 'shop.tiktok.com', formula: 'GOYANG', avatar: false, gender: null,
        thumb: 'linear-gradient(135deg,#fda4af,#9f1239)', videos: [0, 0, 0],
    },
    {
        id: 'p28', tab: 'flow', group: GROUP_UTAMA, sku: '1729451236774509827',
        name: 'Kemeja Lelaki Korean Style Oversized Cotton',
        host: 'shop.tiktok.com', formula: 'AI8SAAT', avatar: true, gender: 'Men',
        thumb: 'linear-gradient(135deg,#a7f3d0,#047857)', videos: [1, 1, 0],
    },

    /* Rak lain — tanpa ini menukar group memaparkan senarai kosong dan
       ciri itu mustahil dinilai. */
    {
        id: 'p7', tab: 'flow', group: 'raya', sku: '1729474882910337712',
        name: 'Kurta Lelaki Slim Fit Raya Cotton Linen',
        host: 'shop.tiktok.com', formula: 'GOYANG', avatar: false, gender: null,
        thumb: 'linear-gradient(135deg,#5eead4,#0f766e)', videos: [1, 0, 0],
    },
    {
        id: 'p8', tab: 'flow', group: 'raya', sku: '1729473640228851199',
        name: 'Set Kuih Raya Tart Nenas Homemade 500g',
        host: 'shop.tiktok.com', formula: 'AI8SAAT', avatar: true, gender: 'Women',
        thumb: 'linear-gradient(135deg,#fdba74,#c2410c)', videos: [0, 0, 0],
    },
    {
        id: 'p9', tab: 'tiktok', group: 'raya', sku: '1729472507713449021',
        name: 'Langsir Blackout Siap Jahit 2 Panel Premium',
        host: 'shop.tiktok.com', formula: 'HOME NICHE', avatar: false, gender: null,
        thumb: 'linear-gradient(135deg,#c4b5fd,#5b21b6)', videos: [1, 1, 0],
    },
    {
        id: 'p10', tab: 'flow', group: 'skin', sku: '1729471338840226615',
        name: 'Serum Vitamin C Brightening Glow 30ml',
        host: 'shop.tiktok.com', formula: 'AI8SAAT', avatar: true, gender: 'Women',
        thumb: 'linear-gradient(135deg,#fde68a,#a16207)', videos: [1, 0, 0],
    },
    {
        id: 'p11', tab: 'tiktok', group: 'skin', sku: '1729470195534118307',
        name: 'Sunscreen SPF50 PA++++ Gel Ringan 50ml',
        host: 'shop.tiktok.com', formula: 'GOYANG', avatar: false, gender: null,
        thumb: 'linear-gradient(135deg,#93c5fd,#1d4ed8)', videos: [1, 1, 1],
    },
    {
        id: 'p12', tab: 'chatgpt', group: 'skin', sku: '1729469072281554493',
        name: 'Retinol Night Cream Anti-Aging 20g',
        host: 'shop.tiktok.com', formula: 'FLOWNITY', avatar: true, gender: 'Women',
        thumb: 'linear-gradient(135deg,#f0abfc,#86198f)', videos: [0, 0, 0],
    },
];

/* Teks contoh untuk kad terbuka. Cukup panjang untuk menunjukkan rupa
   sebenar medan bila penuh — prompt sebenar memang sepanjang ini. */
const HOOKS = [
    'Minyak wangi cepat hilang bila keluar rumah?',
    'Wangi tahan lama walau ke mana korang pergi.',
    'Satu pen je, tapi bau macam mahal?',
];

const teksSet = (nama, n) => ({
    hook: HOOKS[n - 1],
    /* Hook diselitkan betul-betul ke dalam teks ini — bukan hiasan.
       Modal menandakannya dengan warna supaya pelanggan boleh sahkan
       hook dia memang masuk ke dalam prompt (kelakuan sama seperti
       HighlightTextarea dalam WorkspaceView hidup). */
    imej: `Vertical 9:16 TikTok-style image of a young Malaysian Malay woman wearing a soft beige hijab, on-screen text reads "${HOOKS[n - 1]}", holding ${nama} close to her collarbone, natural window light from the left, shallow depth of field, photorealistic, 85mm lens look.`,
    skrip: [
        '0–2s (Hook): Bau perfume cepat hilang bila korang keluar?\n2–5s: Aku dah try pen ni tiga minggu.\n5–8s: Sampai malam pun masih ada bau.',
        '0–2s (Hook): Nak wangi tahan lama sepanjang hari?\n2–5s: Saiz kecil, senang masuk beg.\n5–8s: Sapu sikit je kat pergelangan.',
        '0–2s (Hook): Kecil je, tapi bau macam mahal doh?\n2–5s: Harga bawah RM30 tau.\n5–8s: Link kat bawah, stok tinggal sikit.',
    ][n - 1],
    video: `Visual description: 9:16, Duration 8s. Half-body waist-up framing of a young Malaysian Malay woman in a beige hijab. She lifts ${nama}, applies it to her wrist, then smiles at camera. Soft natural light, slow push-in, no text overlay.`,
    caption: `Wangi tahan lama, saiz kecik senang bawa ✨ #tiktokshopmalaysia #perfume #fyp`,
});

/* Tab pertama BUKAN lagi penapis "semua produk" — ia tempat kerja
   menyediakan prompt (arahan user 2026-08-09). Nilainya kekal `all`
   supaya penanda `p.tab` sedia ada tidak perlu diubah; hanya labelnya
   dan kandungannya yang berbeza.

   Tab ketiga: label "TikTok" → "Schedule" (arahan user 2026-08-10).
   Dua sebab. Pertama, tab ini bukan lagi milik satu destinasi — ia
   tempat menyiarkan untuk KEDUA-DUA jenis affiliate: produk TikTok Shop
   ke TikTok, produk Shopee ke Threads dan Facebook, jadi nama "TikTok"
   akan menipu sebaik produk Shopee masuk. Kedua, pemilih set DIBUANG
   daripada panel — set kini datang daripada jadual, jadi kerja di tab
   ini ialah menetapkan BILA, bukan sekadar menekan hantar.

   ⚠️ Nilainya KEKAL `tiktok` — sama sebab macam `all` di atas. Penanda
   `p.tab` ini tertulis dalam puluhan ribu dokumen produk sedia ada;
   menukar nilainya bermakna migrasi data, bukan tukar label. Nama
   pemboleh ubah dalam fail ini pula ikut LABEL (`tabGenerate` untuk
   nilai `all`), jadi `tabSchedule` untuk nilai `tiktok`. */
/* Agent AI duduk SELEPAS Generate, bukan di hujung (arahan user 2026-08-11).
   Susunan ini mengikut turutan kerja sebenar — Generate → Agent AI → Flow →
   Schedule — dan sama dengan susunan butang Mark dalam bar pukal, jadi mata
   tidak perlu belajar dua susunan berbeza untuk perkara yang sama.

   ⚠️ Workspace sebenar (WorkspaceView.jsx) memakai susunan yang SAMA. Kalau
      salah satu diubah, ubah kedua-duanya — kalau tidak prototaip dan halaman
      sebenar bercanggah, dan rujukan reka bentuk hilang maknanya. */
/* `labelKey` menyimpan KUNCI kamus, bukan ayat — corak yang SAMA
   dengan BULK_ACTIONS di bawah. ⚠️ `value` ialah penanda `p.tab`
   dalam data sebenar; ia BUKAN teks UI dan tidak pernah diterjemah. */
const TAB_OPTIONS = [
    { value: 'all', labelKey: 'workspace.page.tab.generate' },
    { value: 'chatgpt', labelKey: 'workspace.page.tab.agentAi' },
    { value: 'flow', labelKey: 'workspace.page.tab.flow' },
    { value: 'tiktok', labelKey: 'workspace.page.tab.schedule' },
];

/* Kunci simpanan bagi keadaan Control Panel (sorok / papar). Ia disimpan
   dalam browser, bukan Firestore, atas sebab yang SAMA seperti rel menu
   StudioShell: ini keutamaan ruang skrin, dan skrin berbeza pada setiap
   komputer yang pengguna guna. Kunci berasingan daripada `sesaat_studio_rail`
   supaya menyorok panel tidak sekali gus menguncupkan rel menu.

   ⭐ DIEKSPORT dan dipakai juga oleh halaman SEBENAR (App.jsx). Satu
      takrifan sahaja: dua salinan rentetan bermakna satu hari nanti salah
      satu diubah dan pengguna dapat dua tetapan berlainan yang mengawal
      benda yang sama. */
export const CP_KEY = 'sesaat_cp_v39';

/* Tindakan pukal. `danger` sahaja yang dibenarkan berwarna —
   selebihnya menyepi, ikut prinsip satu aksen.

   `labelKey` menyimpan KUNCI kamus, bukan ayat: pemalar aras-modul
   tidak boleh memanggil useT(), jadi terjemahan berlaku pada masa
   render di dalam BulkBar. */
const BULK_ACTIONS = [
    /* Retry didahulukan (arahan user 2026-08-10). Ia berpindah ke sini
       daripada baris kad kerana ia kerja PEMULIHAN: bila satu larian gagal,
       ia jarang gagal pada satu kad sahaja — yang dikehendaki ialah mencuba
       semula kesemuanya sekali gus. Itu peraturan bulk-first dalam CLAUDE.md
       ("setiap aksi larian/pemulihan WAJIB ada laluan borong").

       Kedudukan AWAL, jauh daripada Del Video/Delete yang merah: Retry ialah
       butang yang ditekan dalam keadaan tergesa selepas sesuatu gagal, dan
       itu waktu paling mudah tersilap tekan butang jiran. */
    { id: 'retry', labelKey: 'workspace.bulk.retry', icon: RotateCcw },
    { id: 'flow', labelKey: 'workspace.bulk.markFlow', icon: GitMerge },
    { id: 'tiktok', labelKey: 'workspace.bulk.markSchedule', icon: Send },
    { id: 'group', labelKey: 'workspace.bulk.moveGroup', icon: Folder },
    { id: 'avatar', labelKey: 'workspace.bulk.avatar', icon: Smile },
    { id: 'dup', labelKey: 'workspace.bulk.duplicate', icon: Copy },
    { id: 'save', labelKey: 'workspace.bulk.saveCloud', icon: CloudUpload },
    { id: 'delvid', labelKey: 'workspace.bulk.delVideo', icon: VideoOff, danger: true },
    { id: 'del', labelKey: 'workspace.bulk.delete', icon: Trash2, danger: true },
];

/* ---------- Susunan senarai ------------------------------------------
   TIGA jenis sahaja; arah ditukar dengan menekan jenis yang SAMA sekali
   lagi (keputusan user 2026-08-10). Workspace lama menyenaraikan ENAM
   baris berasingan — naik dan turun bagi setiap jenis — jadi menunya dua
   kali panjang untuk bilangan pilihan sebenar yang sama.

   `lalai` ialah arah yang paling kerap dikehendaki bagi jenis itu, dipakai
   apabila pengguna BERTUKAR jenis. Tanpa ini, menukar ke "Masa" akan
   memberi produk paling lama di atas — hampir tidak pernah itu yang
   dicari; orang menyusun ikut masa untuk melihat kerja yang baru dibuat. */
/* `labelKey` / `tipKunci` menyimpan KUNCI kamus, bukan ayat — pemalar
   aras-modul tidak boleh memanggil useT(). Senarai siap-terjemah
   dibina sekali dalam halaman (`susunPilihan`) dan dihantar kepada
   KEDUA-DUA SusunMenuV39, supaya tiada dua salinan. ⚠️ `id` ialah
   penanda dalaman susunan — jangan terjemah. */
const SUSUN = [
    { id: 'nombor', labelKey: 'workspace.page.sort.number', lalai: 'asc', tipKunci: { asc: 'workspace.page.sort.numberAsc', desc: 'workspace.page.sort.numberDesc' } },
    { id: 'masa', labelKey: 'workspace.page.sort.time', lalai: 'desc', tipKunci: { asc: 'workspace.page.sort.timeAsc', desc: 'workspace.page.sort.timeDesc' } },
    { id: 'module', labelKey: 'workspace.page.sort.module', lalai: 'asc', tipKunci: { asc: 'workspace.page.sort.moduleAsc', desc: 'workspace.page.sort.moduleDesc' } },
];

/* Pembanding susunan kad. DIKONGSI oleh senarai kad biasa dan senarai
   produk tab Schedule — dua salinan bermakna satu daripadanya akan
   ketinggalan senyap bila peraturan susunan berubah. */
function bandingKad(jenis, arah) {
    const banding = {
        nombor: (a, b) => String(a.sku).localeCompare(String(b.sku), undefined, { numeric: true }),
        masa: (a, b) => CONTOH.indexOf(a) - CONTOH.indexOf(b),
        module: (a, b) => (a.formula || '').localeCompare(b.formula || ''),
    }[jenis] || ((a, b) => CONTOH.indexOf(a) - CONTOH.indexOf(b));

    const darab = arah === 'asc' ? 1 : -1;
    return (a, b) => banding(a, b) * darab;
}

/* Kawalan susunan DIPINDAHKAN ke [SusunMenuV39.jsx] — modal Add Product
   memakai kawalan yang SAMA (arahan user 2026-08-14), dan dua salinan
   akan terpesong senyap sebaik peraturan susunan berubah di satu tempat
   sahaja. Senarai jenis (`SUSUN` di atas) kekal di sini kerana ia milik
   senarai ini: kad workspace boleh disusun ikut Module, produk library
   tidak. */

/* ---------- Bahagian kecil ------------------------------------------ */

/* Bingkai video 9:16 — bentuk sama dengan video TikTok yang dihasilkan. */
function VideoSlot({ ada }) {
    return (
        <div style={{
            /* 52 × 90 — saiz SAMA dengan thumbnail collapsed workspace lama
               (WorkspaceView.jsx, "sesaat-vid-thumb"). Keputusan user
               2026-08-10: petak 34 × 60 terlalu kecil untuk mengenal video
               sepintas lalu. Hanya SAIZ yang disalin — radius, border dan
               warna kekal token v3.9. */
            width: 52, height: 90, borderRadius: 8, flex: 'none',
            border: '1px solid var(--s-line)',
            background: ada ? 'var(--s-fill-hover)' : 'var(--s-ground)',
            display: 'grid', placeItems: 'center',
        }}>
            {ada
                ? <Play size={18} strokeWidth={1.8} style={{ color: 'var(--s-ink-2)' }} />
                : <span style={{ fontSize: 13, color: 'var(--s-ink-3)' }}>—</span>}
        </div>
    );
}

/* Kotak semak — dibina sendiri supaya ia ikut token, bukan warna
   lalai browser yang terang. */
function Tick({ on, onChange }) {
    return (
        <button
            type="button"
            role="checkbox"
            aria-checked={on}
            onClick={(e) => { e.stopPropagation(); onChange(); }}
            style={{
                width: 16, height: 16, flex: 'none', cursor: 'pointer', padding: 0,
                borderRadius: 5, display: 'grid', placeItems: 'center',
                border: `1px solid ${on ? 'var(--s-select)' : 'var(--s-line-strong)'}`,
                background: on ? 'var(--s-select)' : 'transparent',
            }}
        >
            {on && (
                <svg width="9" height="9" viewBox="0 0 12 12" fill="none">
                    <path d="M2 6.4L4.7 9L10 3.4" stroke="#111" strokeWidth="2.2"
                        strokeLinecap="round" strokeLinejoin="round" />
                </svg>
            )}
        </button>
    );
}

/* ---------- Kad terbuka --------------------------------------------- */

/* Satu medan prompt. Butang salin muncul bila dituding sahaja — satu set
   ada lima medan, tiga set bermakna lima belas butang; kalau semuanya
   kekal kelihatan, ia menenggelamkan teks yang sepatutnya dibaca.

   ⛔ SENGAJA MONOKROM. Halaman asal memberi setiap medan warnanya sendiri
      (kuning hook, biru image, hijau skrip, kelabu video). Dengan tiga set
      bersebelahan itu jadi dua belas kotak berwarna serentak — masalah
      yang sama seperti pil berwarna pada kad tertutup. Label dan kedudukan
      baris sudah cukup membezakannya: medan yang sama sentiasa sebaris
      merentas ketiga-tiga set. */
function MedanPrompt({ label, nilai, onUbah, mono, tinggi = 76, kunci, disalin, onSalin }) {
    const t = useT();
    return (
        <div className="s-promptbox" style={{ marginBottom: 12 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 5 }}>
                <span style={{
                    fontSize: 10, fontWeight: 600, letterSpacing: '.09em',
                    textTransform: 'uppercase', color: 'var(--s-ink-3)',
                }}>{label}</span>
                <button
                    type="button"
                    className="s-promptbox__copy"
                    aria-label={t('workspace.copyField', { medan: label })}
                    title={t('workspace.copyField', { medan: label })}
                    onClick={(e) => { e.stopPropagation(); onSalin(kunci, nilai); }}
                    style={{
                        padding: 2, background: 'none', border: 0, cursor: 'pointer',
                        color: disalin === kunci ? 'var(--s-ok)' : 'var(--s-ink-3)',
                        display: 'grid', placeItems: 'center',
                    }}
                >
                    {disalin === kunci ? <Check size={12} strokeWidth={2.2} /> : <Copy size={12} strokeWidth={1.9} />}
                </button>
            </div>
            <textarea
                readOnly={!onUbah}
                value={nilai}
                onChange={(e) => onUbah?.(e.target.value)}
                onClick={(e) => e.stopPropagation()}
                style={{
                    width: '100%', height: tinggi, resize: 'vertical',
                    background: 'var(--s-ground)', color: 'var(--s-ink-2)',
                    border: '1px solid var(--s-line)', borderRadius: 'var(--s-r-sm)',
                    padding: '8px 10px', fontSize: 12, lineHeight: 1.6, outline: 'none',
                    fontFamily: mono ? 'var(--s-mono)' : 'inherit',
                }}
            />
        </div>
    );
}

/* Slot video dalam kad terbuka — kosong jadi kawasan muat naik, ada video
   jadi bingkai 9:16 dengan butang main. */
function VideoBesar({ ada }) {
    const t = useT();
    return (
        <div style={{
            aspectRatio: '9 / 16', borderRadius: 'var(--s-r)', marginBottom: 14,
            border: ada ? '1px solid var(--s-line)' : '1px dashed var(--s-line-strong)',
            background: ada ? 'var(--s-fill)' : 'transparent',
            display: 'grid', placeItems: 'center', cursor: 'pointer',
        }}>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 7 }}>
                {ada
                    ? <Play size={20} strokeWidth={1.7} style={{ color: 'var(--s-ink-2)' }} />
                    : <UploadCloud size={20} strokeWidth={1.5} style={{ color: 'var(--s-ink-3)' }} />}
                <span style={{
                    fontSize: 10, fontWeight: 600, letterSpacing: '.09em',
                    textTransform: 'uppercase', color: 'var(--s-ink-3)',
                }}>
                    {ada ? t('workspace.videoReady') : t('workspace.uploadVideo')}
                </span>
            </div>
        </div>
    );
}

/* ---------- Modal butiran produk ------------------------------------
   ⛔ KEPUTUSAN REKA BENTUK (user, 2026-08-07): senarai TIDAK membesar.
      Baris kekal baris; semua butiran hidup dalam modal ini.
      Sebabnya: satu produk ada 3 set × 5 medan = 15 medan teks. Membuka
      itu di dalam senarai menolak produk lain jauh ke bawah sampai
      senarai mustahil diimbas — cubaan pertama membuat kad terbuka
      setinggi 900px lebih.

   SATU modal untuk KETIGA-TIGA kerja: tonton video, muat naik video,
   baca dan edit prompt. Ketiga-tiganya berlaku dalam satu urutan
   fikiran yang sama (tengok hasil → rasa hook tak kena → betulkan
   prompt → run semula). Memecahkannya kepada tiga modal berasingan
   memaksa pengguna tutup-buka-tutup sedangkan dia tidak pernah
   bertukar tugas.

   Papan kekunci: Esc tutup · ← → tukar set. */
/* Baris matriks. Susunan MENGIKUT modal hidup dengan sengaja:
   Hook bersebelahan Image, Script bersebelahan Video — kerana pasangan
   itu bersambung dua hala dalam sistem sebenar (edit Hook → Image
   berubah; edit Script → Video berubah). Meletakkannya bersebelahan
   menjadikan hubungan itu kelihatan.

   'senyap' = baris rujukan yang jarang dibaca (teksnya hampir serupa
   merentas ketiga-tiga set kerana ia dijana daripada template yang
   sama). Ia dipendekkan satu baris, bukan disorok — pelanggan masih
   perlu menyalinnya untuk ditampal ke Flow. */
/* `labelKey` menyimpan KUNCI kamus, bukan ayat — pemalar aras-modul
   tidak boleh memanggil useT(); label diterjemah dalam ProductModal.
   ⚠️ `id` ialah penanda medan dalaman — jangan terjemah. */
const BARIS_SET = [
    { id: 'hook', labelKey: 'workspace.page.field.hook' },
    { id: 'imej', labelKey: 'workspace.page.field.image', mono: true, senyap: true, sorotHook: true },
    { id: 'skrip', labelKey: 'workspace.page.field.script' },
    { id: 'video', labelKey: 'workspace.page.field.video', mono: true, senyap: true },
    { id: 'caption', labelKey: 'workspace.page.field.caption' },
];

const BARIS_SCENE = [
    { id: 'imej', labelKey: 'workspace.page.field.image', mono: true },
    { id: 'skrip', labelKey: 'workspace.page.field.dialog' },
    { id: 'video', labelKey: 'workspace.page.field.video', mono: true, senyap: true },
    { id: 'caption', labelKey: 'workspace.page.field.caption' },
];

/* Tandakan hook di dalam teks lain. Padanan literal sahaja — kalau
   pelanggan sudah mengedit hook sehingga ia tidak lagi wujud dalam
   prompt, tiada apa ditandakan, dan itulah isyaratnya. */
function Sorot({ teks, cari }) {
    if (!cari || !teks.includes(cari)) return <>{teks}</>;
    const [dulu, ...baki] = teks.split(cari);
    return <>{dulu}<mark className="s-mx__hit">{cari}</mark>{baki.join(cari)}</>;
}

/* ---------- Modal butiran produk ------------------------------------
   ⛔ SUSUNAN MATRIKS (pilihan user, 2026-08-07): baris ialah MEDAN,
      lajur ialah SET. Label ditulis sekali di tepi kiri, bukan diulang
      tiga kali seperti modal hidup.

      Kenapa bukan tab: tab memaksa tiga klik hanya untuk menyemak, dan
      membanding tiga set jadi mustahil — pengguna terpaksa mengingat.
      Dengan matriks, membaca Hook ketiga-tiga set ialah satu baris mata.

   Video diletak sebagai baris PERTAMA, sejajar tepat dengan lajurnya,
   supaya tiada kekeliruan video mana milik set mana.

   Klik petak → petak itu jadi editor DI TEMPATNYA dan baris itu
   membesar; tiga set kekal bersebelahan supaya kau masih boleh
   membanding sambil mengedit.

   ⚠️ Yang WAJIB dibawa masuk bila menyambung ke data sebenar nanti
      (semuanya sudah ada dalam WorkspaceView hidup — jangan tertinggal):
        · sync dua hala Hook↔Image dan Script↔Video
        · amaran bila sync gagal (hookSyncWarnings)
        · baris jejak template (templateTrace)
        · tetapan space: showSet2 / showSet3 / showCaption + label tersuai
        · overlay memuatkan Split V2 sebelum teks tiba
        · AI16SAAT ada Script+Video untuk Scene 1 DAN Scene 2 (7 medan) */
function ProductModal({ p, onClose, disalin, onSalin }) {
    const t = useT();
    const nScene = p.formula === 'FLOWNITY' ? 5 : 0;
    const lajur = nScene || 3;
    const baris = nScene ? BARIS_SCENE : BARIS_SET;

    const [draf, setDraf] = useState({});
    const [buka, setBuka] = useState(null);   // "medanId:index" petak yang sedang diedit
    const [qn, setQn] = useState(false);

    useEffect(() => {
        const onKey = (e) => {
            if (e.key !== 'Escape') return;
            /* Esc menutup petak yang terbuka dahulu, barulah modal —
               kalau tidak satu ketukan Esc membuang kerja edit tanpa amaran. */
            if (buka) { setBuka(null); return; }
            onClose();
        };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [onClose, buka]);

    const kunci = (medan, n) => p.id + '_' + (nScene ? 'sc' : 's') + n + '_' + medan;
    const asal = (medan, n) => teksSet(p.name, nScene ? ((n - 1) % 3) + 1 : n)[medan];
    const nilai = (medan, n) => {
        const k = kunci(medan, n);
        return draf[k] !== undefined ? draf[k] : asal(medan, n);
    };
    const diubah = Object.keys(draf).length > 0;

    const lebarGrid = '68px repeat(' + lajur + ', minmax(0, 1fr))';

    return (
        <div className="s-modal" role="dialog" aria-modal="true" aria-label={t('workspace.modal.aria', { nama: p.name })} onClick={onClose}>
            <div className="s-modal__panel s-modal__panel--wide" onClick={(e) => e.stopPropagation()}>

                <header className="s-modal__head">
                    <div style={{
                        width: 30, height: 30, borderRadius: 8, flex: 'none',
                        background: p.thumb, border: '1px solid var(--s-line)',
                    }} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{
                            fontSize: 14, fontWeight: 600, color: 'var(--s-ink)',
                            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                        }}>{p.name}</div>
                        <div className="s-num">#{p.sku} · {p.formula}</div>
                    </div>

                    {/* Quick Notes — nota bertanda kilat di halaman Notes muncul
                        di sini. Panel MENGECUTKAN modal, tidak menutupinya:
                        pelanggan perlu membaca nota SAMBIL menulis prompt. */}
                    <Btn
                        size="sm"
                        variant="ghost"
                        onClick={() => setQn((v) => !v)}
                        icon={<StickyNote size={14} strokeWidth={1.9} />}
                        style={qn ? { color: 'var(--s-brand)', borderColor: 'var(--s-brand)' } : undefined}
                    >
                        {t('workspace.page.quickNotes')}
                    </Btn>

                    <button type="button" onClick={onClose} aria-label={t('workspace.close')} title={t('workspace.closeEsc')} className="s-modal__x">
                        <X size={16} strokeWidth={2} />
                    </button>
                </header>

                <div className="s-modal__split">
                    <div className="s-modal__matrix">

                        {/* Baris video — sejajar dengan lajur set di bawahnya */}
                        <div className="s-mx__row s-mx__row--video" style={{ gridTemplateColumns: lebarGrid }}>
                            <div />
                            {Array.from({ length: nScene ? 1 : lajur }, (_, x) => x + 1).map((n) => {
                                const ada = nScene ? p.videos[0] === 1 : p.videos[n - 1] === 1;
                                return (
                                    <div
                                        key={n}
                                        className="s-mx__cell"
                                        style={nScene ? { gridColumn: '2 / -1', maxWidth: 190 } : undefined}
                                    >
                                        <div className={ada ? 's-mx__vid' : 's-mx__vid s-mx__vid--kosong'}>
                                            {ada
                                                ? <Play size={20} strokeWidth={1.7} />
                                                : <UploadCloud size={19} strokeWidth={1.5} />}
                                        </div>
                                        <div style={{
                                            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                            gap: 6, marginTop: 8,
                                        }}>
                                            <span className="s-mx__lbl">{nScene ? t('workspace.page.field.video') : t('workspace.page.setN', { n })}</span>
                                            <span style={{
                                                width: 5, height: 5, borderRadius: 99,
                                                background: ada ? 'var(--s-ok)' : 'var(--s-line-strong)',
                                            }} />
                                        </div>
                                    </div>
                                );
                            })}
                        </div>

                        {/* Baris medan */}
                        {baris.map((b) => {
                            /* Label diterjemah SEKALI di sini — ia dipakai pada
                               label baris dan pada tooltip butang salin. */
                            const labelBaris = t(b.labelKey);
                            return (
                            <div key={b.id} className="s-mx__row" style={{ gridTemplateColumns: lebarGrid }}>
                                <div className="s-mx__rowlabel"><span className="s-mx__lbl">{labelBaris}</span></div>

                                {Array.from({ length: lajur }, (_, x) => x + 1).map((n) => {
                                    const k = kunci(b.id, n);
                                    const teks = nilai(b.id, n);
                                    const sedangEdit = buka === b.id + ':' + n;

                                    if (sedangEdit) {
                                        return (
                                            <div key={n} className="s-mx__cell s-mx__cell--edit">
                                                <textarea
                                                    autoFocus
                                                    value={teks}
                                                    onChange={(e) => setDraf((d) => ({ ...d, [k]: e.target.value }))}
                                                    onBlur={() => setBuka(null)}
                                                    className={b.mono ? 's-mx__ta s-mx__ta--mono' : 's-mx__ta'}
                                                />
                                            </div>
                                        );
                                    }

                                    return (
                                        <div
                                            key={n}
                                            className="s-mx__cell s-mx__cell--klik"
                                            role="button"
                                            tabIndex={0}
                                            title={t('workspace.clickToEdit')}
                                            onClick={() => setBuka(b.id + ':' + n)}
                                            onKeyDown={(e) => { if (e.key === 'Enter') setBuka(b.id + ':' + n); }}
                                        >
                                            <div
                                                className={
                                                    's-mx__teks'
                                                    + (b.mono ? ' s-mx__teks--mono' : '')
                                                    + (b.senyap ? ' s-mx__teks--senyap' : '')
                                                }
                                            >
                                                {b.sorotHook
                                                    ? <Sorot teks={teks} cari={nilai('hook', n)} />
                                                    : teks}
                                            </div>

                                            <button
                                                type="button"
                                                className="s-mx__copy"
                                                aria-label={t('workspace.copySetField', { medan: labelBaris, n })}
                                                title={t('workspace.copySetField', { medan: labelBaris, n })}
                                                onClick={(e) => { e.stopPropagation(); onSalin(k, teks); }}
                                            >
                                                {disalin === k
                                                    ? <Check size={12} strokeWidth={2.2} />
                                                    : <Copy size={12} strokeWidth={1.9} />}
                                            </button>
                                        </div>
                                    );
                                })}
                            </div>
                            );
                        })}
                    </div>

                    {qn && (
                        <aside className="s-modal__qn">
                            <div style={{
                                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                gap: 8, marginBottom: 11,
                            }}>
                                <span className="s-mx__lbl">{t('workspace.page.quickNotes')}</span>
                                <button type="button" onClick={() => setQn(false)} aria-label={t('workspace.closeQuickNotes')} className="s-modal__x" style={{ width: 22, height: 22 }}>
                                    <X size={12} strokeWidth={2} />
                                </button>
                            </div>

                            {/* Baca sahaja — sama seperti QuickNotesPanel hidup.
                                Nak edit nota, pergi halaman Notes. */}
                            {[
                                { t: 'Hook yang menjadi', b: ['Korang tau tak kenapa serum ni laku gila?', 'Stok tinggal sikit je kat bawah ni.'] },
                                { t: 'Caption gaya soalan', b: ['Siapa lagi yang muka berminyak macam aku dulu?'] },
                            ].map((n) => (
                                <div key={n.t} className="s-modal__qncard">
                                    <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--s-ink)', marginBottom: 6 }}>{n.t}</div>
                                    {n.b.map((baris1, x) => (
                                        <div key={x} className="s-noteline" style={{ display: 'flex', gap: 6, alignItems: 'flex-start' }}>
                                            <span style={{ flex: 1, minWidth: 0, fontSize: 11.5, color: 'var(--s-ink-2)', lineHeight: 1.55 }}>{baris1}</span>
                                            <button
                                                type="button"
                                                className="s-noteline__copy"
                                                aria-label={t('workspace.copyLine')}
                                                title={t('workspace.copyLine')}
                                                onClick={() => onSalin('qn_' + n.t + x, baris1)}
                                                style={{ padding: 2, background: 'none', border: 0, cursor: 'pointer', color: 'var(--s-ink-3)' }}
                                            >
                                                <Copy size={11} strokeWidth={1.9} />
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            ))}

                            <p className="s-dim" style={{ marginTop: 10 }}>
                                {t('workspace.qnFoot')}
                            </p>
                        </aside>
                    )}
                </div>

                <footer className="s-modal__foot">
                    <span className="s-dim">
                        {diubah ? t('workspace.modal.unsaved') : t('workspace.modal.hint')}
                    </span>
                    <div style={{ display: 'flex', gap: 8 }}>
                        <Btn size="sm" variant="ghost" onClick={onClose}>{t('workspace.close')}</Btn>
                        <Btn size="sm" onClick={onClose}>{t('common.save')}</Btn>
                    </div>
                </footer>

            </div>
        </div>
    );
}

/* Satu produk = satu BARIS. Nama menonjol; segala maklumat lain
   turun jadi teks bertitik pada satu baris. */
/* Dieksport untuk prototaip /workspace-v4 (tab My Agent + Flow, 18 Okt) — tiada perubahan kelakuan. */
export function ProductRow({ p, selected, onToggle, last, onOpen }) {
    const t = useT();
    /* Label "Avatar" DIBUANG daripada baris chip (arahan user 2026-08-10):
       gambar avatar itu sendiri sudah memberitahu kad ini ada avatar, jadi
       chip bertulis "Avatar" hanya mengulang apa yang mata sudah nampak.
       Gender pun keluar dari sini — ia berpindah ke bawah gambar avatar,
       kerana gender itu memerihalkan AVATAR, bukan produk; duduk jauh
       daripada avatar ia kelihatan seperti ciri produk. */
    const meta = [p.formula].filter(Boolean);
    const siap = p.videos.filter(Boolean).length;

    /* Gradien mewakili foto avatar sebenar (prototaip tiada fail gambar).
       Ia BUKAN hiasan — sebab itu ia dibenarkan walaupun v3.9 monokrom,
       sama seperti gambar produk di sebelahnya. */
    const avatarBg = p.avatarThumb || (p.gender === 'Men'
        ? 'linear-gradient(135deg,#93c5fd,#1e3a8a)'
        : 'linear-gradient(135deg,#f0abfc,#86198f)');

    /* Link Flow collection HANYA muncul selepas kad pernah di-Run
       (keputusan user 2026-08-10) — kad baharu memang belum ada collection,
       dan link mati pada baris ini cuma jadi benda yang tak menjadi bila
       ditekan.

       ⚠️ PROTOTAIP: "pernah di-Run" dikira daripada kewujudan video, kerana
          data contoh tidak menyimpan collection. Pada data SEBENAR syaratnya
          ialah medan `product.collectionUrl` — ditulis oleh extension selepas
          Run (background.ts). Jangan bawa kiraan-daripada-video ini ke data
          sebenar: kad boleh sudah ada collection sebelum video pertama siap,
          dan kad salinan (duplicate) sengaja LAHIR TANPA collectionUrl
          walaupun mewarisi segala-galanya yang lain. */
    const adaCollection = !!p.collectionUrl || p.videos.some(Boolean);

    const pautan = [
        {
            teks: t('workspace.page.link.tiktok'),
            url: `https://shop.tiktok.com/view/product/${p.sku}`,
            tip: t('workspace.link.tiktokTip'),
        },
        adaCollection && {
            teks: t('workspace.page.link.flow'),
            url: p.collectionUrl || 'https://labs.google/fx/tools/flow',
            tip: t('workspace.link.flowTip'),
        },
    ].filter(Boolean);

    /* Klik baris membuka modal, bukan menanda pilihan — keputusan user
       2026-08-07 (idea 4). Menanda pilihan dipindah ke kotak semak, dan
       "Pilih semua" ditambah di atas senarai supaya kerja pukal tidak
       jadi lebih perlahan; itu peraturan bulk-first dalam CLAUDE.md. */
    return (
        <div
            onClick={onOpen}
            className="s-row-v39"
            style={{
                display: 'flex', alignItems: 'center', gap: 12, cursor: 'pointer',
                /* Padding menegak 12px mengikut kad workspace lama. Digandingkan
                   dengan petak video 90px, tinggi baris jadi ~114px — sama
                   seperti kad lama. Padding melintang kekal nilai v3.9 kerana
                   baris ini menamatkan dengan bulatan chevron; padding kanan
                   44px kad lama akan menolaknya masuk ke tengah. */
                padding: '12px 14px 12px 15px',
                borderBottom: last ? 'none' : '1px solid var(--s-line)',
                background: selected ? 'var(--s-fill)' : 'transparent',
                boxShadow: selected ? 'inset 2px 0 0 var(--s-select)' : 'none',
            }}
        >
            <Tick on={selected} onChange={onToggle} />

            {/* Gambar + teks dibungkus SATU bekas `stretch` supaya kedua-dua
                petak gambar setinggi blok teks di sebelahnya — dari nombor ID
                sampai baris chip (arahan user 2026-08-10).

                Hanya LEBAR yang ditetapkan (68px); tingginya dibiarkan kepada
                `stretch`. Jadi kalau nama produk suatu hari nanti pecah ke dua
                baris, gambar meninggi sendiri dan barisnya kekal rata —
                disahkan dengan ujian: satu baris 68×68, dua baris 68×88.

                ⛔ JANGAN ganti dengan `aspectRatio: 1` untuk "mengekalkan
                   petak persegi". Sudah dicuba dan ia RUNTUH: dalam flex item,
                   aspect-ratio tidak dapat mengira lebar daripada tinggi yang
                   datang dari stretch — petak keluar 2px lebar. Menambah
                   `height: 100%` menjadikannya lebih teruk (2×2).

                ⚠️ Bekas ini WAJIB kekal berasingan daripada baris kad. Baris
                   itu `alignItems: center` kerana petak video 90px dan bulatan
                   chevron mesti berpusat; menukarnya kepada `stretch` akan
                   meregangkan video dan chevron sekali. */}
            <div style={{
                display: 'flex', alignItems: 'stretch', gap: 12,
                flex: 1, minWidth: 0,
            }}>
                <div style={{
                    width: 68, borderRadius: 11, flex: 'none',
                    background: p.thumb, border: '1px solid var(--s-line)',
                }} />

                {/* Avatar — SAIZ DAN BENTUK SAMA dengan gambar produk.
                    Percubaan pertama membuatnya bulat dan lebih kecil supaya
                    "orang" nampak berbeza daripada "barang"; user menolaknya —
                    bulatan di sebelah segi empat tidak pernah kelihatan sejajar
                    pada mata (tepi bulatan mengecil, jadi ia nampak terapung),
                    dan dua saiz berlainan menjadikan barisnya sendeng.

                    Gender duduk DI DALAM gambar sebagai jalur bawah, bukan teks
                    di bawahnya: teks di luar menambah tinggi pada satu petak
                    sahaja, dan itulah yang dahulunya menolak avatar naik. */}
                {p.avatar && (
                <div style={{
                    width: 68, borderRadius: 11, flex: 'none',
                    background: avatarBg, border: '1px solid var(--s-line)',
                    position: 'relative', overflow: 'hidden',
                }}>
                    {p.gender && (
                        <span style={{
                            position: 'absolute', left: 0, right: 0, bottom: 0, height: 15,
                            /* Lapisan gelap, bukan warna tema — jalur ini duduk di
                               atas GAMBAR yang warnanya tidak boleh diramal, jadi
                               warna teksnya mesti putih tetap supaya kekal terbaca
                               pada avatar cerah mahupun gelap. */
                            background: 'rgba(0,0,0,.62)', color: '#fff',
                            fontSize: 9, fontWeight: 600, lineHeight: 1,
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}>
                            {/* Perkataan SAHAJA — tiada simbol ♀/♂ di sebelahnya.
                                Diukur: ikon + teks makan 46.4px dalam jalur 42px,
                                jadi "Women" keluar terpotong. Membuang ikon ialah
                                pilihan yang betul, bukan sekadar yang muat: apabila
                                perkataannya sudah tertulis penuh, simbol itu cuma
                                mengulang maklumat yang sama. */}
                            {p.gender}
                        </span>
                    )}
                </div>
                )}

            <div style={{ flex: 1, minWidth: 0 }}>
                <div className="s-num" style={{ lineHeight: 1.4 }}>#{p.sku}</div>

                <div style={{
                    fontSize: 15.5, fontWeight: 600, color: 'var(--s-ink)',
                    letterSpacing: '-.012em', lineHeight: 1.3,
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                }}>
                    {p.name}
                </div>

                {/* Tiga kumpulan, dipisah garis: DESTINASI keluar · TINDAKAN
                    dalam app · LABEL. Semua memakai chip yang sama rupa —
                    lihat `.s-rowchip` dalam ui.css untuk sebabnya. */}
                <div style={{
                    display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap',
                    marginTop: 6,
                }}>
                    {/* Destinasi didahulukan kerana ia menjawab soalan yang
                        lebih awal difikirkan: "produk mana ni?" dan "video aku
                        ada di mana?". Dahulunya baris ini hanya memapar teks
                        mati "shop.tiktok.com" — nama hos yang SAMA pada setiap
                        kad, jadi ia tidak pernah membezakan apa-apa. */}
                    {pautan.map((l) => (
                        <a
                            key={l.teks}
                            className="s-rowchip s-rowchip--klik"
                            href={l.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            title={l.tip}
                        >
                            <Link2 size={12} strokeWidth={1.9} style={{ flex: 'none' }} />
                            {l.teks}
                        </a>
                    ))}

                    <span className="s-rowsep" />

                    {/* "Full prompt" DIKEKALKAN walaupun klik baris membuka modal
                        yang sama (keputusan user 2026-08-10): sebahagian pengguna
                        mencari butang dan tidak sedar baris itu sendiri boleh
                        diklik. "Retry" pula sudah BERPINDAH ke bar pukal — ia
                        kerja pemulihan yang jarang dilakukan pada satu kad
                        sahaja; selepas larian gagal, yang dikehendaki ialah
                        mencuba semula kesemuanya sekali gus. */}
                    {/* ⚠️ Pemboleh ubah gelung dinamakan `teksChip`, BUKAN `t` —
                        `t` di sini ialah fungsi terjemahan, dan gelung yang
                        memakai nama itu menutupnya secara senyap. */}
                    {[
                        t('workspace.page.chip.fullPrompt'),
                        t('workspace.page.chip.extension'),
                    ].map((teksChip) => (
                        <button
                            key={teksChip}
                            type="button"
                            className="s-rowchip s-rowchip--klik"
                            onClick={(e) => e.stopPropagation()}
                        >
                            {teksChip}
                        </button>
                    ))}

                    {meta.length > 0 && <span className="s-rowsep" />}

                    {meta.map((t) => (
                        <span key={t} className="s-rowchip">{t}</span>
                    ))}
                </div>
            </div>
            </div>

            <span className="s-dim" style={{ flex: 'none' }}>{siap}/3</span>

            <div style={{ display: 'flex', gap: 12, flex: 'none' }}>
                {p.videos.map((v, i) => <VideoSlot key={i} ada={!!v} />)}
            </div>

            <span
                aria-hidden="true"
                style={{
                    width: 28, height: 28, borderRadius: 99, flex: 'none',
                    border: '1px solid var(--s-line)', display: 'grid', placeItems: 'center',
                }}
            >
                <ChevronRight size={15} strokeWidth={1.9} style={{ color: 'var(--s-ink-3)' }} />
            </span>
        </div>
    );
}

/* Bar tindakan — satu baris sahaja, teks senyap, silang di hujung. */
function BulkBar({ count, semua, onToggleSemua, onClear }) {
    const t = useT();
    return (
        <div style={{
            display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
            padding: '9px 12px', marginBottom: 12,
            background: 'var(--s-card)', border: '1px solid var(--s-line)',
            borderRadius: 'var(--s-r)',
        }}>
            {/* Pilih semua duduk DI SINI, bukan di atas senarai (keputusan
                user 2026-08-09). Baris berasingan di atas senarai hanya
                memegang kotak semak ini seorang diri — satu baris penuh
                untuk satu kotak. Laluan pukal bermula sama: tandakan satu
                baris, bar ini muncul, barulah "semua" masuk akal. */}
            <label style={{
                display: 'inline-flex', alignItems: 'center', gap: 7,
                cursor: 'pointer', fontSize: 12.5, color: 'var(--s-ink-2)',
            }}>
                <Tick on={semua} onChange={onToggleSemua} />
                {t('workspace.bulk.selectAll')}
            </label>
            <span style={{ width: 1, height: 14, background: 'var(--s-line-strong)' }} />

            {/* SATU kunci penuh, bukan nombor + perkataan berasingan:
                susunan perkataan berbeza antara bahasa, jadi ayat yang
                dicantum daripada serpihan akan pecah dalam bahasa lain. */}
            <span style={{ fontSize: 13, color: 'var(--s-ink)' }}>
                {t('workspace.bulk.selected', { n: count })}
            </span>
            <span style={{ width: 1, height: 14, background: 'var(--s-line-strong)' }} />

            {BULK_ACTIONS.map((a) => {
                const Icon = a.icon;
                return (
                    <button
                        key={a.id}
                        type="button"
                        style={{
                            display: 'inline-flex', alignItems: 'center', gap: 5,
                            padding: 0, background: 'none', border: 0, cursor: 'pointer',
                            fontFamily: 'inherit', fontSize: 12.5,
                            color: a.danger ? 'var(--s-stop)' : 'var(--s-ink-2)',
                        }}
                    >
                        <Icon size={13} strokeWidth={1.9} />
                        {t(a.labelKey)}
                    </button>
                );
            })}

            <button
                type="button"
                onClick={onClear}
                aria-label={t('workspace.bulk.clear')}
                title={t('workspace.bulk.clear')}
                style={{
                    marginLeft: 'auto', width: 24, height: 24, padding: 0, cursor: 'pointer',
                    background: 'none', border: 0, display: 'grid', placeItems: 'center',
                    color: 'var(--s-ink-3)',
                }}
            >
                <X size={14} strokeWidth={2} />
            </button>
        </div>
    );
}

/* ============================================================
   PANEL LARIAN
   ============================================================
   Kawalan automation yang dahulunya duduk di kaki panel Extension
   (Video Mode, Image Mode, In Project, Collection, Sets, Run Flow).

   Bentuknya mengikut penyunting jenis acara app.cal.com:
     · bar tajuk = nama panel di kiri, TINDAKAN UTAMA di kanan atas,
       sama seperti "Secret meeting … [Save]"
     · badan = seksyen dipisah garis nipis; setiap medan label di
       atas, kawalan penuh lebar, nota bantuan di bawahnya
     · tiada kotak berwarna, tiada bayang — kedalaman daripada garis

   Kenapa panel duduk di KANAN dan bukan mengambil seluruh halaman
   seperti Cal: tetapan di sini dikenakan ke atas produk yang DIPILIH
   dalam senarai. Kalau panel menutup senarai, pengguna hilang
   pandangan pada benda yang dia sedang tetapkan. Cal boleh guna
   halaman penuh kerana ia menyunting satu acara yang berdiri sendiri.

   ⚠️ PROTOTAIP — butang Run tidak melancarkan apa-apa.
   ============================================================ */

/* Nilai MESTI sama persis dengan nama dalam dropdown Google Flow —
   skrip automation memadan mengikut nama, bukan mengikut kod. */
const VIDEO_MODES = [
    { value: 'omni_flash_6s', label: 'Omni Flash 6s' },
    { value: 'omni_flash_8s', label: 'Omni Flash 8s' },
    { value: 'omni_flash_10s', label: 'Omni Flash 10s' },
    { value: 'lite', label: 'Veo 3.1 — Lite' },
    { value: 'fast', label: 'Veo 3.1 — Fast' },
    { value: 'lower_priority', label: 'Veo 3.1 — Lite [Lower Priority] · Ultra sahaja' },
];

const IMAGE_MODES = ['Nano Banana Pro', 'Nano Banana 2', 'Nano Banana 2 Lite'];

const FLOW_URL_PREFIX = 'https://labs.google/fx/tools/flow/project/';

/* Satu seksyen tetapan. Tajuk boleh ditinggalkan bila seksyen itu
   sudah jelas dengan sendirinya. */
function Sect({ title, children }) {
    return (
        <div className="s-sect">
            {title && <p className="s-sect__title">{title}</p>}
            {children}
        </div>
    );
}

/* Label di atas, kawalan di bawah, nota bantuan di bawah sekali —
   susunan yang SAMA untuk setiap medan dalam panel. */
function Medan({ label, tanda, nota, children }) {
    return (
        <div>
            {label && <span className="s-lbl">{label}{tanda}</span>}
            {children}
            {nota && <p className="s-hint">{nota}</p>}
        </div>
    );
}

function Suis({ on, onChange, disabled, label }) {
    return (
        <button
            type="button" role="switch" aria-checked={!!on} aria-label={label}
            disabled={disabled} onClick={onChange}
            className={cx('s-switch', on && 's-switch--on')}
        >
            <span className="s-switch__dot" />
        </button>
    );
}

/* Baris suis: penerangan di kiri, suis di kanan hujung. `lock` menukar
   suis kepada mangga — untuk ciri yang memang belum dibuka. */
function BarisSuis({ label, tanda, nota, on, onChange, disabled, lock }) {
    return (
        <div className="s-togrow">
            <div className="s-togrow__txt">
                <span className="s-lbl" style={{ marginBottom: 0, opacity: lock ? 0.5 : 1 }}>
                    {label}{tanda}
                </span>
                {nota && <p className="s-hint">{nota}</p>}
            </div>
            {lock
                ? <Lock size={15} strokeWidth={1.9} style={{ color: 'var(--s-ink-3)', flex: 'none', marginTop: 3 }} />
                : <Suis on={on} onChange={onChange} disabled={disabled} label={label} />}
        </div>
    );
}

/* Tanda "Admin" pada medan yang pelanggan biasa tidak nampak. Ia ada
   di sini semata-mata supaya keseluruhan panel boleh dinilai sekali
   lalu — pada halaman sebenar medan itu tidak dirender langsung. */
/* Ia KOMPONEN, bukan pemalar JSX seperti dahulu: pemalar aras-modul
   tidak boleh memanggil useT(), jadi teksnya perlu diterjemah pada
   masa render. Dipakai sebagai `tanda={<TandaAdmin />}`. */
function TandaAdmin() {
    const t = useT();
    return <Badge tone="brand" style={{ fontSize: 11, padding: '0 6px' }}>{t('workspace.page.adminBadge')}</Badge>;
}

function PilSet({ senarai, dipilih, onToggle, label }) {
    const t = useT();
    return (
        <div style={{ display: 'flex', gap: 8 }} role="group" aria-label={label}>
            {senarai.map((s, i) => (
                <button
                    key={s}
                    type="button"
                    aria-pressed={dipilih.includes(s)}
                    onClick={() => onToggle(s)}
                    className={cx('s-pill', dipilih.includes(s) && 's-pill--on')}
                >
                    {t('workspace.page.setPill', { n: i + 1 })}
                </button>
            ))}
        </div>
    );
}

/* `mod` datang DARIPADA tab halaman, bukan daripada suis di dalam panel
   ini (arahan user 2026-08-09). Dahulu ada dua tempat memilih Flow lawan
   TikTok — tab di atas senarai, dan satu lagi Segmented di dalam kad ini
   — jadi pengguna boleh berada pada tab Flow sambil melihat tetapan
   TikTok. Sekarang tab itulah satu-satunya suis. */
function RunPanel({ mod = 'flow', dipilih, kadDipilih = [], jadual = [], jadualId, setJadualId, onUrusJadual, onSorok = null }) {
    const t = useT();

    /* Flow */
    const [videoMode, setVideoMode] = useState('lower_priority');
    const [imageMode, setImageMode] = useState('Nano Banana Pro');
    const [flowUrl, setFlowUrl] = useState('');
    const [fokusUrl, setFokusUrl] = useState(null);
    const [sambungCollection, setSambungCollection] = useState(false);
    const [multiBrowser, setMultiBrowser] = useState(true);
    const [sets, setSets] = useState(['set1', 'set2', 'set3']);

    /* TikTok */
    const [postSahaja, setPostSahaja] = useState(false);
    const [showcase, setShowcase] = useState(false);
    const [berjadual, setBerjadual] = useState(false);

    /* Saluran hantar. SUIS, bukan tab (arahan user 2026-08-10) — tab
       bermaksud "satu pada satu masa", jadi pengguna terpaksa menekan Run
       sekali bagi setiap saluran. Itu melanggar prinsip satu-klik-borong.
       Suis boleh hidup serentak dan satu Run melayan semuanya.

       Threads dan Facebook DIKUNCI buat masa ini — automationnya belum
       dibina. Ia sengaja dibiar KELIHATAN dan bukan disorok: kalau
       disorok, pengguna tidak tahu ia akan datang; kalau dibiar terbuka,
       dia menekannya dan gagal. */
    const [tiktokOn, setTiktokOn] = useState(true);

    /* "In project" dan "Collection" berkongsi SATU nilai — sama seperti
       halaman sebenar. Sebabnya URL collection bermula dengan awalan
       project yang sama, jadi automation melayan kedua-duanya serupa;
       yang berbeza hanya kotak mana yang memegang nilai itu. Mengisi
       satu kotak mengunci kotak satu lagi. */
    const collectionMemegang = !!flowUrl && flowUrl.includes('/collection/');
    const projectMemegang = !!flowUrl && !collectionMemegang;
    const medanAktif = fokusUrl || (collectionMemegang ? 'collection' : (projectMemegang ? 'project' : null));

    const lepasFokus = (e) => {
        setFokusUrl(null);
        /* Buang apa-apa yang bukan link Flow. Membiarkannya bermakna
           automation dibuka pada halaman yang tidak wujud. */
        const nilai = e.target.value.trim();
        if (nilai && !nilai.startsWith(FLOW_URL_PREFIX)) setFlowUrl('');
    };

    const kotakUrl = (jenis, placeholder) => {
        const aktif = medanAktif === jenis;
        return (
            <div style={{ position: 'relative' }}>
                <input
                    className="s-input"
                    type="text"
                    disabled={jenis === 'project' ? collectionMemegang : projectMemegang}
                    value={aktif ? flowUrl : ''}
                    onFocus={() => setFokusUrl(jenis)}
                    onChange={(e) => setFlowUrl(e.target.value.trim())}
                    onBlur={lepasFokus}
                    placeholder={placeholder}
                    style={aktif && flowUrl ? { paddingRight: 30 } : undefined}
                />
                {aktif && flowUrl && (
                    <button
                        type="button"
                        onClick={() => { setFlowUrl(''); setFokusUrl(null); }}
                        aria-label={t('workspace.clear')}
                        title={t('workspace.clear')}
                        style={{
                            position: 'absolute', right: 8, top: 10, width: 16, height: 16,
                            padding: 0, background: 'none', border: 0, cursor: 'pointer',
                            display: 'grid', placeItems: 'center', color: 'var(--s-ink-3)',
                        }}
                    >
                        <X size={13} strokeWidth={2} />
                    </button>
                )}
            </div>
        );
    };

    const togglePil = (senarai, set) => (s) =>
        set(senarai.includes(s) ? senarai.filter((x) => x !== s) : [...senarai, s].sort());

    const isFlow = mod === 'flow';

    /* Jadual yang sedang dipilih. Kalau ia dipadam semasa modal terbuka,
       jatuh ke jadual pertama supaya panel tidak kekal menunjuk kepada
       sesuatu yang sudah tiada. */
    const jadualAktif = jadual.find((j) => j.id === jadualId) || jadual[0] || null;

    /* Pratonton dikira daripada kad yang BETUL-BETUL ditandakan, bukan
       daripada kiraan kasar — set yang tiada video dilangkau, jadi
       "10 kad" belum tentu "10 video". */
    const kiraJadual = useMemo(() => {
        if (!berjadual || !jadualAktif || kadDipilih.length === 0) return null;
        const video = susunVideo(kadDipilih, jadualAktif);
        if (!video.length) return { jumlah: 0 };
        const masa = letakMasa(jadualAktif, video.length);
        /* Slot tetap tanpa jam bertanda — tiada masa untuk diberikan. */
        if (!masa.length) return { jumlah: 0, tiadaMasa: true };
        const akhir = masa[masa.length - 1];
        const mungkinDilangkau = kadDipilih.length * jadualAktif.sets.length - video.length;
        return {
            jumlah: video.length,
            mungkinDilangkau,
            mula: tarikhLabel(masa[0].hari) + ' · ' + minitKeJam(masa[0].minit),
            akhir: tarikhLabel(akhir.hari) + ' · ' + minitKeJam(akhir.minit),
            lebihHad: akhir.hari >= HAD_HARI_TIKTOK,
        };
    }, [berjadual, jadualAktif, kadDipilih]);

    return (
        /* Tiada had tinggi dan tiada tatal dalaman: panel ini kini duduk
           di dalam lajur Control Panel yang menatal sendiri. Dua tatal
           bersarang bermakna pengguna terpaksa meneka mana satu yang
           akan bergerak bila dia menggolek tetikus. */
        /* Kad tetapan bertajuk — bahagian BAWAH Control Panel. Kad ini
           dan kad "Produk & prompt" di atas guna bentuk yang sama tetapi
           tajuk dan perihalnya berbeza; itulah yang memberitahu pengguna
           bahawa ini dua kerja berlainan, bukan satu senarai panjang. */
        <div className="s-panelbox">

            <div className="s-cardhead">
                <span style={{ flex: 1, minWidth: 0 }}>
                    {/* `Hide` duduk di KIRI tajuk, di dalam panel yang ia sorokkan
                        (arahan user 2026-08-15).

                        Kenapa satu perkataan sudah cukup di sini, sedangkan butang
                        di jalur tab wajib berteks penuh: perkataan "Control Panel"
                        berada betul-betul di sebelahnya, jadi baris ini terbaca
                        sebagai satu ayat — "Hide Control Panel". Di jalur tab panel
                        itu sudah tiada untuk menamakan dirinya.

                        ⛔ Butang diletak sebagai ADIK-BERADIK tajuk, bukan di dalam
                           `<p class="s-cardhead__t">`. Di dalam tajuk ia mewarisi
                           saiz dan tebal tajuk, jadi ia keluar sebagai butang yang
                           berlainan daripada setiap butang lain di halaman ini —
                           masalah yang sama pernah berlaku pada pemilih group dalam
                           kepala halaman. */}
                    <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        {onSorok ? (
                            /* ⚗️ UJIAN (arahan user 2026-08-15): tajuk DIMASUKKAN ke
                               dalam kotak butang — seluruh baris tajuk menjadi satu
                               kawalan. "Hide" kekal sebagai kata kerja di hadapan
                               supaya kotak itu terbaca sebagai arahan, bukan sekadar
                               label yang kebetulan berkotak. */
                            <button
                                onClick={onSorok}
                                title={t('workspace.panel.hideTip')}
                                style={{
                                    display: 'inline-flex', alignItems: 'center', gap: 6,
                                    height: 28, padding: '0 10px', borderRadius: 8,
                                    border: '1px solid var(--s-line-strong)',
                                    background: 'transparent', cursor: 'pointer',
                                    whiteSpace: 'nowrap', flex: 'none',
                                }}
                            >
                                <PanelRightClose size={13} strokeWidth={2} style={{ color: 'var(--s-ink-3)' }} />
                                <span style={{ fontSize: 12, fontWeight: 500, color: 'var(--s-ink-3)' }}>{t('workspace.page.hide')}</span>
                                <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--s-ink)' }}>{t('workspace.page.controlPanel')}</span>
                            </button>
                        ) : (
                            <p className="s-cardhead__t">{t('workspace.page.controlPanel')}</p>
                        )}
                    </span>
                </span>
            </div>

            {/* Badan — seksyen dipisah garis nipis. */}
            <div>
                {isFlow ? (
                    <>
                        {/* ★ [2026-11-03] Workflow — dropdown kaya (lakaran 1 user). Didahulukan
                            sebelum Model, sama seperti Control Panel sebenar. Lihat WorkflowPilihV39.jsx. */}
                        <WorkflowPilihV39 />

                        {/* "Mod penjanaan" ditukar kepada "Model" — perkataan
                            "penjanaan" di sini menyebabkan ia terbaca seolah-olah
                            milik kad Produk & prompt di atas, sedangkan ini
                            pemilih model Google Flow. */}
                        <Sect title={t('workspace.page.sect.model')}>
                            <Medan
                                label={t('workspace.page.run.videoLabel')}
                                nota={t('workspace.run.videoNote')}
                            >
                                <PilihV39
                                    ariaLabel={t('workspace.page.run.videoModelAria')}
                                    nilai={videoMode}
                                    onPilih={setVideoMode}
                                    pilihan={VIDEO_MODES.map((m) => ({ id: m.value, label: m.label }))}
                                />
                            </Medan>

                            <Medan label={t('workspace.page.run.imageLabel')}>
                                <PilihV39
                                    ariaLabel={t('workspace.page.run.imageModelAria')}
                                    nilai={imageMode}
                                    onPilih={setImageMode}
                                    pilihan={IMAGE_MODES.map((m) => ({ id: m, label: m }))}
                                />
                            </Medan>
                        </Sect>

                        <Sect title={t('workspace.page.sect.destination')}>
                            <Medan
                                label={t('workspace.page.run.inProject')}
                                nota={collectionMemegang
                                    ? t('workspace.run.projectBlocked')
                                    : projectMemegang
                                        ? t('workspace.run.projectUsed')
                                        : t('workspace.run.projectEmpty')}
                            >
                                {kotakUrl('project', t('workspace.run.projectPlaceholder'))}
                            </Medan>

                            <Medan
                                label={t('workspace.page.run.collection')}
                                tanda={<TandaAdmin />}
                                nota={projectMemegang
                                    ? t('workspace.run.collectionBlocked')
                                    : collectionMemegang
                                        ? t('workspace.run.collectionUsed')
                                        : t('workspace.run.collectionEmpty')}
                            >
                                {kotakUrl('collection', t('workspace.run.collectionPlaceholder'))}
                            </Medan>

                            <BarisSuis
                                label={t('workspace.page.run.continueCollection')}
                                tanda={<TandaAdmin />}
                                nota={t('workspace.run.continueCollectionNote')}
                                on={sambungCollection}
                                onChange={() => setSambungCollection((v) => !v)}
                            />
                        </Sect>

                        <Sect title={t('workspace.page.sect.set')}>
                            <Medan nota={t('workspace.run.setNote')}>
                                <PilSet
                                    label={t('workspace.page.sect.set')}
                                    senarai={['set1', 'set2', 'set3']}
                                    dipilih={sets}
                                    onToggle={togglePil(sets, setSets)}
                                />
                            </Medan>
                        </Sect>

                        <Sect title={t('workspace.page.sect.advanced')}>
                            <BarisSuis
                                label={t('workspace.page.run.multiBrowser')}
                                tanda={<TandaAdmin />}
                                nota={multiBrowser
                                    ? t('workspace.run.multiBrowserOn')
                                    : t('workspace.run.multiBrowserOff')}
                                on={multiBrowser}
                                onChange={() => setMultiBrowser((v) => !v)}
                            />
                            <BarisSuis
                                label={t('workspace.page.sect.advanced')}
                                nota={t('workspace.run.proOnly')}
                                lock
                            />
                        </Sect>
                    </>
                ) : (
                    <>
                        {/* Saluran DAHULU, barulah tetapannya. Pengguna memilih
                            "ke mana" sebelum "macam mana" — terbalik daripada
                            itu bermakna dia menetapkan Showcase sebelum tahu
                            Showcase hanya wujud pada TikTok. */}
                        <Sect title={t('workspace.page.sect.postTo')}>
                            <BarisSuis
                                label={t('workspace.page.post.tiktok')}
                                nota={t('workspace.post.tiktokNote')}
                                on={tiktokOn}
                                onChange={() => setTiktokOn((v) => !v)}
                            />
                            <BarisSuis label={t('workspace.page.post.threads')} lock />
                            <BarisSuis label={t('workspace.page.post.facebook')} lock />
                            <p className="s-hint">
                                {t('workspace.post.lockedNote')}
                            </p>
                        </Sect>

                        <Sect title={t('workspace.page.sect.postMode')}>
                            <BarisSuis
                                label={t('workspace.page.post.postOnly')}
                                nota={t('workspace.post.postOnlyNote')}
                                on={postSahaja}
                                onChange={() => setPostSahaja((v) => { if (!v) setShowcase(false); return !v; })}
                            />
                            <BarisSuis
                                label={t('workspace.page.post.showcaseOnly')}
                                nota={t('workspace.post.showcaseNote')}
                                on={showcase}
                                onChange={() => setShowcase((v) => {
                                    if (!v) { setPostSahaja(false); setBerjadual(false); }
                                    return !v;
                                })}
                            />
                        </Sect>

                        <Sect title={t('workspace.page.sect.schedule')}>
                            <BarisSuis
                                label={t('workspace.page.post.scheduledPost')}
                                nota={t('workspace.post.scheduledNote')}
                                on={berjadual}
                                disabled={showcase}
                                onChange={() => setBerjadual((v) => !v)}
                            />

                            {berjadual && (
                                jadual.length === 0 ? (
                                    <Btn variant="ghost" size="sm" onClick={onUrusJadual} icon={<Plus size={14} strokeWidth={2} />}>
                                        {t('workspace.page.schedule.createFirst')}
                                    </Btn>
                                ) : (
                                    <>
                                        <Medan label={t('workspace.page.schedule.use')}>
                                            <PilihV39
                                                ariaLabel={t('workspace.page.schedule.use')}
                                                nilai={jadualAktif?.id || ''}
                                                onPilih={setJadualId}
                                                pilihan={jadual.map((j) => ({ id: j.id, label: j.nama }))}
                                            />
                                        </Medan>

                                        {/* Tetapan sebenar dipapar sebagai badge. Nama jadual boleh
                                            ditukar sesuka hati dan menjadi mengelirukan kemudian;
                                            baris ini sentiasa bercakap benar. */}
                                        {jadualAktif && (
                                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                                                {/* Parameter map dinamakan `ayat`, bukan `t` — nama `t`
                                                    di sini akan menutup fungsi terjemahan dan sesiapa
                                                    yang menyunting baris ini kemudian akan tertanya-tanya
                                                    kenapa t('...') berhenti berfungsi. */}
                                                {ringkasJadual(jadualAktif, t).map((ayat) => (
                                                    <Badge key={ayat} tone="neutral">{ayat}</Badge>
                                                ))}
                                            </div>
                                        )}

                                        {/* Pratonton — bukti kepada pengguna bahawa dia betul-betul
                                            dapat apa yang dia bayangkan, sebelum Run ditekan. */}
                                        <div style={{
                                            border: '1px solid var(--s-line)',
                                            borderRadius: 'var(--s-r-lg)',
                                            padding: '10px 12px',
                                        }}>
                                            {dipilih === 0 ? (
                                                <p className="s-dim">{t('workspace.preview.pickProducts')}</p>
                                            ) : kiraJadual?.tiadaMasa ? (
                                                <p className="s-dim">
                                                    {t('workspace.preview.noTimes')}
                                                </p>
                                            ) : !kiraJadual || kiraJadual.jumlah === 0 ? (
                                                <p className="s-dim">
                                                    {t('workspace.preview.noVideo')}
                                                </p>
                                            ) : (
                                                <>
                                                    {/* Dua kunci PENUH, bukan ayat asas + hujung yang
                                                        dicantum — bahagian "dilangkau" duduk di tempat
                                                        berlainan dalam bahasa lain. */}
                                                    <p className="s-dim" style={{ marginBottom: 6 }}>
                                                        {kiraJadual.mungkinDilangkau > 0
                                                            ? t('workspace.preview.countWithSkip', {
                                                                produk: dipilih,
                                                                video: kiraJadual.jumlah,
                                                                langkau: kiraJadual.mungkinDilangkau,
                                                            })
                                                            : t('workspace.preview.count', {
                                                                produk: dipilih,
                                                                video: kiraJadual.jumlah,
                                                            })}
                                                    </p>
                                                    <p className="s-num" style={{ fontSize: 13, color: 'var(--s-ink-2)' }}>
                                                        {kiraJadual.mula} → {kiraJadual.akhir}
                                                    </p>
                                                    {kiraJadual.lebihHad && (
                                                        <p className="s-hint" style={{ color: 'var(--s-warn)' }}>
                                                            {t('workspace.preview.overLimit', { hari: HAD_HARI_TIKTOK })}
                                                        </p>
                                                    )}
                                                </>
                                            )}
                                        </div>

                                        <Btn
                                            variant="quiet"
                                            size="sm"
                                            onClick={onUrusJadual}
                                            icon={<CalendarClock size={14} strokeWidth={2} />}
                                        >
                                            {t('workspace.page.schedule.manage')}
                                        </Btn>
                                    </>
                                )
                            )}
                        </Sect>

                        {/* Seksyen "Set" DIBUANG (arahan user 2026-08-10) — set
                            kini ditetapkan melalui jadual, bukan dipilih di sini.
                            Dua tempat memilih set bermakna dua sumber kebenaran,
                            dan pengguna tidak akan tahu yang mana satu menang.
                            Tiada maklumat hilang: barisan badge jadual di atas
                            memang sudah menyebut setnya ("Semua set" / "Set 1 +
                            Set 2") melalui `ringkasJadual`. */}
                    </>
                )}
            </div>

            {/* Jalur kaki — kiraan di kiri, tindakan utama di kanan.
                Bentuk yang sama dengan kad Produk & prompt di atas, dan
                dengan "Asia/Kuala_Lumpur … Edit availability" pada kad
                Availability Cal. */}
            <div className="s-cardfoot">
                <span className="s-dim" style={{ flex: 1, minWidth: 0 }}>
                    {dipilih === 0
                        ? t('workspace.page.run.noSelection')
                        : t('workspace.page.run.selectedCount', { n: dipilih })}
                </span>
                {/* Butang tidak lagi menyebut TikTok — ia menghantar ke
                    mana-mana channel yang dihidupkan. Mematikan semua channel
                    mematikan butang, sama seperti tidak menanda produk: dua-dua
                    bermakna tiada kerja untuk dijalankan. */}
                <Btn
                    size="sm"
                    style={{ flex: 'none' }}
                    disabled={dipilih === 0 || (!isFlow && !tiktokOn)}
                    title={dipilih === 0
                        ? t('workspace.run.needProduct')
                        : (!isFlow && !tiktokOn) ? t('workspace.run.needChannel') : undefined}
                    icon={isFlow ? <Play size={14} strokeWidth={2} /> : <Send size={14} strokeWidth={2} />}
                >
                    {isFlow ? t('workspace.page.run.runFlow') : t('workspace.page.run.runPost')}
                </Btn>
            </div>
        </div>
    );
}

/* ---------- Halaman -------------------------------------------------- */

export default function WorkspaceV39View({ onNavigate }) {
    const t = useT();
    const [tab, setTab] = useState('all');
    const [dipilih, setDipilih] = useState(() => new Set());
    const [modalId, setModalId] = useState(null);
    const [disalin, setDisalin] = useState(null);

    /* Senarai siap-terjemah bagi pemalar aras-modul (TAB_OPTIONS, SUSUN).
       Ia dibina SEKALI di sini, bukan di tempat render, kerana kawalan
       susunan muncul pada DUA tempat — dua salinan bermakna satu
       daripadanya akan tertinggal bila senarai berubah. */
    const tabOptions = useMemo(
        () => TAB_OPTIONS.map((o) => ({ value: o.value, label: t(o.labelKey) })),
        [t],
    );

    const susunPilihan = useMemo(
        () => SUSUN.map((s) => ({
            id: s.id,
            lalai: s.lalai,
            label: t(s.labelKey),
            tip: { asc: t(s.tipKunci.asc), desc: t(s.tipKunci.desc) },
        })),
        [t],
    );

    /* ---------- Control Panel: sorok / papar --------------------------
       SATU tetapan untuk semua tab yang membawa panel — pengguna yang
       menyoroknya pada tab Flow tidak sepatutnya terpaksa menyorok semula
       sebaik dia beralih ke Schedule. Itu sebabnya keadaan ini duduk di
       peringkat halaman dan bukan di dalam RunPanel.

       Bacaan dan tulisan dibalut try/catch: localStorage boleh melontar
       ralat dalam pelayaran peribadi, dan panel yang hilang bukan alasan
       untuk seluruh Workspace gagal render. */
    const [panelSorok, setPanelSorok] = useState(() => {
        try { return localStorage.getItem(CP_KEY) === 'sorok'; } catch { return false; }
    });
    const tetapkanPanel = (sorok) => {
        setPanelSorok(sorok);
        try { localStorage.setItem(CP_KEY, sorok ? 'sorok' : 'papar'); } catch { /* abaikan */ }
    };

    /* Susunan senarai. Lalai "Masa · baharu dahulu" — sama seperti workspace
       lama, supaya kerja yang baru dijana sentiasa berada di atas. */
    const [susunJenis, setSusunJenis] = useState('masa');
    const [susunArah, setSusunArah] = useState('desc');

    /* ⚠️ PROTOTAIP SAHAJA — butang buka/tutup semua TIADA logik (arahan user
       2026-08-10: "tak perlu logic sbb kita buat kt prototaip je dulu").
       Ia hanya menukar ikonnya sendiri supaya rupa bar itu boleh dinilai.

       ⛔ Sebelum ia disambung ke fungsi sebenar, ada satu perkara yang MESTI
          diputuskan dahulu: v3.9 tiada kad yang membesar dalam senarai —
          semua butiran hidup dalam modal (keputusan user 2026-08-07). Jadi
          "buka semua" belum ada apa-apa untuk dibuka. Cadangan yang belum
          dipilih: anak panah tukar kad di dalam modal. */
    const [bukaSemua, setBukaSemua] = useState(false);

    /* Jadual hidup di sini, bukan di dalam RunPanel — ia dicapai dari DUA
       tempat: butang "Schedule template" di bar tab (buat/edit) dan panel
       TikTok (pilih semasa Run). */
    const [jadual, setJadual] = useState(JADUAL_CONTOH);
    const [jadualId, setJadualId] = useState(JADUAL_CONTOH[0].id);
    const [bukaJadual, setBukaJadual] = useState(false);

    /* ---------- Akaun kalendar (tab Schedule) ------------------------
       DUA akaun contoh, bukan satu — kelakuan yang perlu diuji di sini
       ialah apa yang berlaku semasa BERTUKAR akaun, dan itu mustahil
       dilihat kalau hanya ada satu.

       `susunan` = { [idAkaun]: { hariTemplate, isian } }. Setiap akaun
       memegang kalendarnya sendiri; itulah yang membolehkan set 1 pergi
       ke satu akaun dan set 3 ke akaun lain. */
    const [akaun, setAkaun] = useState([
        { id: 'ak1', nama: 'Akaun 1' },
        { id: 'ak2', nama: 'Akaun 2' },
    ]);
    const [akaunAktif, setAkaunAktif] = useState('ak1');
    const [susunan, setSusunan] = useState({});

    const ubahSusunan = (fn) => setSusunan((prev) => ({
        ...prev,
        [akaunAktif]: fn(prev[akaunAktif] || { hariTemplate: {}, isian: {} }),
    }));

    const ciptaAkaun = (nama) => {
        const baru = akaunBaharu(nama);
        setAkaun((prev) => [...prev, baru]);
        setAkaunAktif(baru.id);
    };

    /* Set yang dipegang akaun LAIN — inilah yang menandakan set 1 dan 2
       sebagai "sudah dijadualkan pada Akaun 1" apabila pengguna beralih
       ke Akaun 2, dan yang membuatkan seretan di sana terus jatuh pada
       set 3. Ia menandakan sahaja; seretan tidak pernah ditolak. */
    const setLuar = useMemo(
        () => setDipegangAkaunLain(susunan, akaunAktif, akaun),
        [susunan, akaunAktif, akaun],
    );

    /* Group. Kad turut menjadi state kerana memadam group MEMINDAHKAN
       kad — ia bukan sekadar membuang nama daripada senarai. */
    const [groupSenarai, setGroupSenarai] = useState(GROUP_CONTOH);
    const [groupAktif, setGroupAktif] = useState(GROUP_UTAMA);
    const [kad, setKad] = useState(CONTOH);
    const [padamGroupId, setPadamGroupId] = useState(null);

    const salin = (kunci, teks) => {
        navigator.clipboard?.writeText(teks);
        setDisalin(kunci);
        setTimeout(() => setDisalin((k) => (k === kunci ? null : k)), 1500);
    };

    /* Kiraan pada setiap baris menu = jumlah produk dalam group itu
       merentas SEMUA tab. Ia menjawab "ada apa dalam rak ini", bukan
       "ada apa pada tab yang sedang dibuka" — sebab itulah ia dikira
       daripada `kad`, bukan daripada `ditapis`.

       Susunan: group utama sentiasa dahulu, selebihnya ikut abjad —
       sama seperti workspace lama, supaya group yang baharu dicipta
       mendarat di tempat yang pengguna boleh jangka. */
    const groupsUntukMenu = useMemo(() => {
        const kira = {};
        kad.forEach((p) => { kira[p.group] = (kira[p.group] || 0) + 1; });
        return groupSenarai
            /* `nama` menang atas `namaKunci`: sebaik pengguna menamakan
               semula sesuatu group, nama itu miliknya — bukan lagi ayat
               kamus. Terjemahan berlaku SEBELUM sort supaya susunan
               abjad mengikut bahasa yang sedang dipapar. */
            .map((g) => ({ ...g, nama: g.nama || t(g.namaKunci), kira: kira[g.id] || 0 }))
            .sort((a, b) => (
                a.id === GROUP_UTAMA ? -1
                    : b.id === GROUP_UTAMA ? 1
                        : a.nama.localeCompare(b.nama)
            ));
    }, [groupSenarai, kad, t]);

    /* Group menapis DAHULU, barulah tab. */
    const dalamGroup = useMemo(() => kad.filter((p) => p.group === groupAktif), [kad, groupAktif]);

    /* Pilihan dikosongkan setiap kali group bertukar — kalau tidak, bar
       pukal membawa kad yang sudah tidak kelihatan di skrin. */
    const pilihGroup = (id) => { setGroupAktif(id); setDipilih(new Set()); };

    const ciptaGroup = (nama) => {
        const id = 'g' + Date.now();
        setGroupSenarai((s) => [...s, { id, nama }]);
        pilihGroup(id);   // terus dibuka, sama seperti workspace lama
    };

    const namaSemulaGroup = (id, nama) =>
        setGroupSenarai((s) => s.map((g) => (g.id === id ? { ...g, nama } : g)));

    /* Padam = pindahkan kad, buang nama, dan bawa pengguna ke rak
       destinasi kalau rak yang dipadam itulah yang sedang dibuka. */
    const padamGroup = (id, destinasi) => {
        setKad((s) => s.map((p) => (p.group === id ? { ...p, group: destinasi } : p)));
        setGroupSenarai((s) => s.filter((g) => g.id !== id));
        setGroupAktif((a) => (a === id ? destinasi : a));
        setPadamGroupId(null);
    };

    /* Tab Generate tiada senarai produk langsung — bukan "semua produk"
       lagi. `ditapis` dikosongkan supaya kiraan, bar pukal dan pilihan
       "Pilih semua" tidak merujuk kepada kad yang tidak kelihatan. */
    const tabGenerate = tab === 'all';

    const ditapis = useMemo(() => {
        if (tab === 'all') return [];
        const senarai = dalamGroup.filter((p) => p.tab === tab);

        /* ⚠️ PROTOTAIP: "Masa" disusun mengikut kedudukan dalam senarai
              contoh, kerana data contoh tiada medan tarikh. Pada data
              SEBENAR gunakan `movedAt` (jatuh balik ke `addedAt`) — medan
              itulah yang ditulis semasa produk dipindahkan ke workspace,
              dan itu jugalah yang menentukan susunan "Terbaru" di My
              Products. Jangan susun ikut `id`: id produk sebenar bukan
              berturutan mengikut masa. */
        /* ⛔ ID produk TikTok ialah 19 digit — LEBIH BESAR daripada nombor
              selamat JavaScript (16 digit). `Number(sku)` membulatkan hujungnya,
              jadi dua produk yang berbeza pada beberapa digit terakhir akan
              dikira SAMA dan susunannya jadi rawak antara keduanya. Sebab itu
              perbandingan dibuat sebagai TEKS dengan `numeric: true`, yang
              membandingkan digit demi digit tanpa menukarnya kepada nombor
              (dan sekali gus betul untuk ID pendek produk manual: "9" tidak
              lagi dikira lebih besar daripada "10"). */
        return [...senarai].sort(bandingKad(susunJenis, susunArah));
    }, [tab, dalamGroup, susunJenis, susunArah]);

    /* Senarai produk pada tab Schedule. Ia mengambil SELURUH group (bukan
       kad bertanda Schedule sahaja) — produk yang sudah ada video patut
       boleh dijadualkan tanpa perlu ditanda dahulu — tetapi ia tetap
       menghormati kawalan susunan yang kini duduk di atasnya. Kawalan yang
       tidak mengubah apa-apa ialah kawalan palsu. */
    const kadJadual = useMemo(
        () => [...dalamGroup].sort(bandingKad(susunJenis, susunArah)),
        [dalamGroup, susunJenis, susunArah],
    );

    const toggle = (id) => setDipilih((prev) => {
        const next = new Set(prev);
        next.has(id) ? next.delete(id) : next.add(id);
        return next;
    });

    /* Kiraan pilihan hanya mengira baris yang MEMANG kelihatan —
       kalau tidak, bar papar "3 dipilih" sedangkan skrin kosong. */
    const kadDipilih = useMemo(() => ditapis.filter((p) => dipilih.has(p.id)), [ditapis, dipilih]);
    const kiraan = kadDipilih.length;

    /* Tab TikTok memaparkan senarai DAN kalendar serentak — seretan
       antara keduanya mustahil kalau salah satu tersembunyi.

       Template TIDAK lagi dipilih di sini. Setiap hari dalam kalendar
       memilih templatenya sendiri (arahan user 2026-08-09), jadi yang
       dihantar ke bawah ialah SENARAI penuh template — bukan satu
       yang sudah dipilih. */
    const tabSchedule = tab === 'tiktok';

    /* Panel larian milik tab Flow dan TikTok sahaja (arahan user
       2026-08-09). Tab itulah yang menentukan tetapan mana yang dipapar —
       panel tidak lagi mempunyai suis Flow/TikTok sendiri. */
    const adaPanelLarian = tab === 'flow' || tab === 'tiktok';

    return (
        /* Tiga panel, susunan sama seperti penyunting Cal.com:
             rel menu (StudioShell) · Control Panel · senarai produk
           Header merentas kedua-dua lajur; setiap lajur menatal
           sendiri. Lihat `.s-work` dalam design/ui.css untuk ukuran
           yang diambil daripada Cal. */
        <div className="s-work sesaat-m-tight">

            {/* Butang "Add products" DIBUANG daripada kepala halaman —
                ia kini butang "+ Produk" di dalam composer. Dua laluan
                menambah produk yang duduk bersebelahan hanya membuatkan
                pengguna teragak-agak yang mana satu betul.

                Tab pula turun ke lajur kanan: ia menapis senarai produk,
                jadi tempatnya di atas senarai itu — bukan di kepala
                halaman yang turut menaungi Control Panel. */}
            <div className="s-work__head">
                {/* Tiada subtajuk (arahan user 2026-08-07) — kepala halaman ini
                    kekal SATU baris supaya tingginya sama seperti /formula.
                    Workspace ialah tempat kerja harian, bukan halaman yang perlu
                    memperkenalkan dirinya setiap kali dibuka; ayat penerangan
                    hanya menolak kandungan sebenar turun. */}
                {/* Pemilih group duduk DI SINI, bukan di atas senarai bersama
                    tab. Tab menapis senarai sahaja; group menentukan rak mana
                    yang sedang dibuka — termasuk rak mana yang menerima produk
                    baharu daripada composer di lajur sebelah. Meletakkannya di
                    atas senarai bermakna pengguna menambah produk sambil
                    melihat nama rak di lajur yang lain. */}
                {/* ⛔ Pemilih dihantar melalui `sisiTajuk`, BUKAN diselitkan ke
                    dalam `title`. Di dalam tajuk ia mewarisi tebal 600, jarak
                    huruf tajuk dan muka taip saiz-tajuk font Cal Sans — jadi
                    butang dan senarainya keluar berlainan daripada setiap
                    butang lain di halaman ini. Diukur dan disahkan. */}
                {/* ⛔ Butang sorok/papar Control Panel PERNAH duduk di sini (baris
                    tajuk). Ia dipindahkan ke jalur tab, sebelah kanan ikon
                    buka/tutup semua (arahan user 2026-08-15) — di sana ia duduk
                    bersama kawalan paparan yang lain, dan jalur itu melekat
                    semasa menatal sama seperti baris ini. Jangan pindahkan ia
                    balik ke sini tanpa bertanya. */}
                <PageHeader
                    title={t('workspace.page.title')}
                    sisiTajuk={
                        <>
                            <span className="s-chiprule" />
                            <GroupSwitcherV39
                                groups={groupsUntukMenu}
                                aktif={groupAktif}
                                onPilih={pilihGroup}
                                onCipta={ciptaGroup}
                                onNamaSemula={namaSemulaGroup}
                                onMintaPadam={setPadamGroupId}
                            />
                        </>
                    }
                    className="s-work__pagehead"
                    actions={<span className="s-dim">{t('workspace.prototypeNote')}</span>}
                />
            </div>

            <div className="s-work__body">

                {/* ---------- KIRI · Senarai produk ----------
                    Senarai naik ke KIRI dan panel larian turun ke KANAN
                    (arahan user 2026-08-09). Warna dan lebar setiap lajur
                    tidak bertukar — lajur kawalan kekal gelap dan sempit,
                    lajur kerja kekal cerah dan lebar — jadi yang berpindah
                    hanyalah kedudukan, bukan maksudnya. */}
                {/* `paddingTop: 0` — jurang 22px di atas jalur tab kini dibawa
                    oleh JALUR itu sendiri, bukan oleh lajur ini. Sebabnya jalur
                    itu melekat: kalau jurang tinggal pada lajur, ia jalur telus
                    setinggi 22px di atas bar melekat, dan kad yang menatal
                    kelihatan menyelinap di celahnya. Corak yang SAMA dipakai
                    halaman sebenar (`sesaat-ws-toolbar` membawa padding 22px
                    sendiri di dalam bekas tanpa padding atas). */}
                <div className="s-work__right" style={{ paddingTop: 0 }}>

                    {/* ⭐ JALUR INI MELEKAT semasa senarai menatal — sama seperti
                        halaman sebenar (`sesaat-ws-toolbar`, WorkspaceView.jsx).
                        Tanpa ia, prototaip berkelakuan BERBEZA daripada halaman
                        yang ia sepatutnya mewakili, dan itu menjadikannya
                        rujukan reka bentuk yang menipu — butang Control Panel
                        di hujung jalur ini akan hilang sebaik pengguna menatal.

                        Jalur ini membawa `paddingTop: 22` SENDIRI (lajur di
                        sekelilingnya dipaksa `paddingTop: 0`) supaya tepi
                        atasnya betul-betul bertemu tepi atas kawasan menatal.
                        Kalau jurang itu ditinggalkan pada lajur, ia menjadi
                        jalur telus setinggi 22px di atas bar dan kad yang
                        menatal kelihatan menyelinap di celahnya — memang itu
                        yang berlaku pada cubaan pertama. */}
                    <div style={{
                        display: 'flex', alignItems: 'center', gap: 12,
                        flexWrap: 'wrap',
                        position: 'sticky', top: 0, zIndex: 5,
                        background: 'var(--s-panel)',
                        paddingTop: 22, paddingBottom: 16,
                    }}>
                        <Segmented value={tab} onChange={setTab} options={tabOptions} />

                        {/* Lencana "N produk" DIBUANG (arahan user 2026-08-09) —
                            baris produk itu sendiri sudah menunjukkan berapa
                            banyak; nombor itu cuma mengulang apa yang mata
                            sudah nampak. */}

                        {/* Jadual disiapkan TANPA menandakan apa-apa produk — ia
                            peraturan masa, bukan senarai kad. Sebab itu butangnya
                            duduk di bar tab dan bukan di dalam bar pukal.

                            Tab TikTok SAHAJA (arahan user 2026-08-09): jadual di
                            sini bermaksud waktu post TikTok. Flow tidak menyiarkan
                            apa-apa dan Agent AI tidak menjalankan automation, jadi
                            butang ini di sana hanya membuka tetapan yang tidak
                            akan digunakan. */}
                        {/* ── Kelompok KANAN — satu bekas, bukan tiga butang yang
                               masing-masing menolak diri sendiri dengan
                               `marginLeft: auto`.

                               Sebabnya: jiran butang Control Panel bertukar ikut
                               tab (Schedule template di satu tab, susunan +
                               buka/tutup di tab lain, tiada apa-apa di tab
                               ketiga). Dengan satu bekas, butang itu sentiasa
                               berada di HUJUNG SEKALI — kedudukannya tidak
                               melompat setiap kali pengguna tukar tab. */}
                        <div style={{
                            display: 'flex', alignItems: 'center', gap: 8,
                            marginLeft: 'auto',
                        }}>
                        {tabSchedule && (
                            <Btn
                                variant="ghost"
                                size="sm"
                                onClick={() => setBukaJadual(true)}
                                icon={<CalendarClock size={14} strokeWidth={2} />}
                            >
                                {t('workspace.page.scheduleTemplate')}
                            </Btn>
                        )}

                        {/* Susunan + buka/tutup semua duduk di HUJUNG KANAN bar,
                            jauh daripada tab di kiri: tab MENAPIS (produk mana
                            kelihatan), dua ini pula MENGATUR (bagaimana ia
                            disusun dan dipapar). Meletakkannya bersebelahan tab
                            menjadikan enam butang satu deretan tanpa memberitahu
                            mana satu buat apa.

                            Tab Generate tiada senarai produk (kad composer yang
                            mengambil tempatnya), jadi tiada apa untuk disusun.

                            Tab Schedule pula MEMPUNYAI senarai produk, tetapi ia
                            satu daripada DUA grid — jadi kawalan susunan berpindah
                            turun dan duduk terus di atas senarai itu (arahan user
                            2026-08-13). Kawalan yang melayang di jalur atas tidak
                            memberitahu grid mana yang ia susun. */}
                        {!tabGenerate && !tabSchedule && (
                            <div style={{
                                display: 'flex', alignItems: 'center', gap: 4,
                            }}>
                                <SusunMenuV39
                                    pilihan={susunPilihan}
                                    jenis={susunJenis}
                                    arah={susunArah}
                                    onTukar={(j, a) => { setSusunJenis(j); setSusunArah(a); }}
                                />
                                <Btn
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => setBukaSemua((v) => !v)}
                                    title={t(bukaSemua ? 'workspace.collapseAll' : 'workspace.expandAll')}
                                    aria-label={t(bukaSemua ? 'workspace.collapseAll' : 'workspace.expandAll')}
                                    icon={bukaSemua
                                        ? <ChevronsDownUp size={14} strokeWidth={2} />
                                        : <ChevronsUpDown size={14} strokeWidth={2} />}
                                />
                            </div>
                        )}

                        {/* Papar semula Control Panel — HUJUNG SEKALI, sebelah kanan
                            ikon buka/tutup semua (arahan user 2026-08-15).

                            ⭐ Ia muncul HANYA semasa panel tersorok. Butang menyorok
                               duduk di dalam panel itu sendiri (lihat `RunPanel`),
                               jadi semasa panel terbuka jalur ini kekal bersih —
                               pada bila-bila masa hanya SATU daripada dua kawalan
                               itu wujud, jadi tiada dua butang serupa serentak.

                            ⛔ BERTEKS PENUH di sini, bukan ikon sahaja dan bukan
                               "Show" sahaja: panel sudah tiada di skrin, jadi tiada
                               apa-apa yang menamakan benda yang bakal muncul.
                               Butang ini juga SATU-SATUNYA jalan memanggilnya
                               balik. (Ikon-sahaja pernah ditolak pada butang
                               Optimize dalam sidebar — "org tak faham".) */}
                        {adaPanelLarian && panelSorok && (
                            <Btn
                                variant="ghost"
                                size="sm"
                                onClick={() => tetapkanPanel(false)}
                                aria-expanded={false}
                                icon={<PanelRightOpen size={14} strokeWidth={2} />}
                            >
                                {t('workspace.page.showControlPanel')}
                            </Btn>
                        )}
                        </div>
                    </div>

                    {/* Kotak carian DIBUANG (arahan user 2026-08-09) — tab di atas
                        sudah menapis senarai, dan senarai kerja harian tidak
                        sepanjang mana. Baris carian itu hanya menolak senarai
                        sebenar turun. */}

                    {/* Tab Generate = tempat menyediakan prompt, bukan tempat
                        melihat produk. Senarai produk SENGAJA kosong di sini
                        (arahan user 2026-08-09) — kad "Produk & prompt"
                        mengambil tempatnya. Produk yang sudah dijana muncul
                        pada tab Flow / TikTok / Agent AI mengikut tandanya. */}
                    {/* Had lebar 760px DIBUANG (arahan user 2026-08-09) — kad
                        ini mengisi seluruh lajur, sama seperti senarai produk
                        pada tab lain. */}
                    {tabGenerate && <PromptComposerV39 onNavigate={onNavigate} />}

                    {!tabGenerate && !tabSchedule && kiraan > 0 && (
                        <BulkBar
                            count={kiraan}
                            semua={ditapis.length > 0 && ditapis.every((p) => dipilih.has(p.id))}
                            onToggleSemua={() => setDipilih((prev) => {
                                const semua = ditapis.every((p) => prev.has(p.id));
                                const next = new Set(prev);
                                ditapis.forEach((p) => (semua ? next.delete(p.id) : next.add(p.id)));
                                return next;
                            })}
                            onClear={() => setDipilih(new Set())}
                        />
                    )}

                    {tabGenerate ? null : tabSchedule ? (
                        /* ⚠️ REKA BENTUK SAHAJA — kad kalendar ialah RAMALAN
                           yang dikira daripada jadual terpilih, bukan rekod
                           sebenar. Ia menggunakan semua produk contoh yang
                           ada video supaya corak jadual kelihatan penuh;
                           pada halaman sebenar nanti sumbernya ialah rekod
                           video yang benar-benar dihantar. */
                        <JadualTiktokV39
                            templates={jadual}
                            kad={kadJadual}
                            capsyen={(nama, n) => teksSet(nama, n).caption}
                            susunKawalan={
                                <SusunMenuV39
                                    pilihan={susunPilihan}
                                    jenis={susunJenis}
                                    arah={susunArah}
                                    onTukar={(j, a) => { setSusunJenis(j); setSusunArah(a); }}
                                />
                            }
                            susunan={susunan[akaunAktif]}
                            onUbahSusunan={ubahSusunan}
                            akaun={akaun}
                            akaunAktif={akaunAktif}
                            onTukarAkaun={setAkaunAktif}
                            onCiptaAkaun={ciptaAkaun}
                            setLuar={setLuar}
                        />
                    ) : ditapis.length === 0 ? (
                        <EmptyState title={t(dalamGroup.length === 0 ? 'workspace.empty.groupTitle' : 'workspace.empty.tabTitle')}>
                            {dalamGroup.length === 0
                                ? t('workspace.empty.groupDesc')
                                : t('workspace.empty.tabDesc')}
                        </EmptyState>
                    ) : (
                        /* Kad BERISIAN di sini, berbeza daripada kad rata di
                           lajur kiri — ini betul, bukan tidak konsisten.
                           Cal buat perkara yang sama: lajur kirinya rata
                           (borang di atas #141414), manakala kad pratonton
                           di lajur kanan berisian #171717 di atas latar
                           muted yang lebih terang. Kad di sini lebih GELAP
                           daripada latarnya; kad di kiri pula tiada isian
                           langsung. */
                        <div style={{
                            background: 'var(--s-card)',
                            border: '1px solid var(--s-line)',
                            borderRadius: 'var(--s-r-lg)',
                            overflow: 'hidden',
                        }}>
                            {ditapis.map((p, i) => (
                                <ProductRow
                                    key={p.id}
                                    p={p}
                                    selected={dipilih.has(p.id)}
                                    onToggle={() => toggle(p.id)}
                                    last={i === ditapis.length - 1}
                                    onOpen={() => setModalId(p.id)}
                                />
                            ))}
                        </div>
                    )}

                </div>

                {/* ---------- KANAN · Control Panel ----------
                    Hanya pada tab Flow dan TikTok (arahan user 2026-08-09).
                    Tab Generate menyediakan prompt dan tab Agent AI tidak
                    menjalankan automation, jadi panel larian di sana hanya
                    tetapan yang tidak boleh ditekan. Tanpa panel, lajur
                    kerja mengambil seluruh lebar (lihat `:only-child`
                    dalam design/ui.css).

                    Pengguna juga boleh menyoroknya sendiri melalui butang `Hide`
                    pada kepala panel. Semasa tersorok lajur ini KEKAL dalam
                    pokok — ia hanya mengecut ke sifar melalui kelas
                    `--sorok` (lihat design/ui.css). Membuangnya terus daripada
                    pokok bermakna ia lenyap mengejut; kekalkannya membolehkan
                    lebar itu bergerak, dan lajur kerja mengembang mengisi
                    ruangnya dalam gerakan yang sama.

                    ⚠️ Kerana ia kekal dirender, `.s-work__right:only-child`
                       tidak lagi menyala. Itu tidak menjadi masalah: lajur
                       kerja memang membawa `flex-grow: 1` dan menyerap baki
                       ruang dengan sendirinya. */}
                {adaPanelLarian && (
                    <div
                        className={cx('s-work__left', 's-work__left--kanan', panelSorok && 's-work__left--sorok')}
                        aria-hidden={panelSorok || undefined}
                    >
                        <RunPanel
                            onSorok={() => tetapkanPanel(true)}
                            mod={tab}
                            dipilih={kiraan}
                            kadDipilih={kadDipilih}
                            jadual={jadual}
                            jadualId={jadualId}
                            setJadualId={setJadualId}
                            onUrusJadual={() => setBukaJadual(true)}
                        />
                    </div>
                )}

            </div>

            {modalId && (
                <ProductModal
                    p={kad.find((x) => x.id === modalId)}
                    onClose={() => setModalId(null)}
                    disalin={disalin}
                    onSalin={salin}
                />
            )}

            {bukaJadual && (
                <ScheduleTemplateModalV39
                    senarai={jadual}
                    setSenarai={setJadual}
                    kadContoh={kad}
                    /* ⭐ Tanpa `t` modal ini kekal Melayu walaupun halaman
                       sudah bertukar English — satu pulau BM di tengah skrin
                       English, dan ~50 kunci workspace.schedule.* jadi kod
                       mati. Halaman ini v3.9 sahaja, jadi ia dihantar tanpa
                       syarat; pemanggil dalam WorkspaceView.jsx (rupa LAMA
                       masih dilayan di situ) sengaja TIDAK menghantarnya. */
                    t={t}
                    onClose={() => setBukaJadual(false)}
                />
            )}

            {/* Pengesahan padam dirender oleh HALAMAN, bukan oleh pemilih —
                pemilih itu duduk di dalam <h1> kepala halaman, dan lapisan
                penuh skrin tidak boleh hidup di dalam tajuk. */}
            {padamGroupId && (
                <PadamGroupModalV39
                    g={groupsUntukMenu.find((x) => x.id === padamGroupId)}
                    groups={groupsUntukMenu}
                    onBatal={() => setPadamGroupId(null)}
                    onSah={(destinasi) => padamGroup(padamGroupId, destinasi)}
                />
            )}

        </div>
    );
}
